import { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { NODE_TYPE_COLORS } from '@/constants/graph'
import { stripFileExtension } from '@/utils/graphExport'
import { renderAnnotatedPnidBlob, PNID_RENDER_ERROR } from '@/utils/pnidAnnotatedExport'
import { trackEvent } from '@/utils/analytics'
import { useSearchParams } from 'react-router-dom'
import { apiService, type LatestGroundTruthResult } from '@/service/api'
import { useGraphStatusPoll } from '@/hooks/useGraphStatusPoll'
import type { DocumentRecord } from '@/types/catalog'
import { groupCellsIntoTables, coupleWallResize, type TableRegion } from './tableGrid'
import { useAppStore } from '@/store'
import { isFabricEmbedMode } from '@/lib/fabricEmbedAuth'
import PnidRenderer from './PnidRenderer'
import type { PnidZoomHandle } from './PnidRenderer'
import type {
  Rectangle,
  ClickEvent,
  AddEvent,
  DeleteEvent,
} from './PnidRenderer'
import DetailsFlyout from './DetailsFlyout'
import { collectConnectedEdgeIds, buildEntityDeletePatch } from './entityDeletion'
import { bboxOfVertices } from './polygonEdit'
import { trimTrailingDuplicateVertices } from './connectionDraw'
import { nearestPointOnPath, junctionAttachEndpoint } from './shapeSnap'
import { junctionConnectors, linesEndingAtSymbol, symbolsAtLineEnds } from './lineAttach'
import ControlPanel from './ControlPanel'
import { ModeType, NodeType } from '@/types/graph'
import { panelConfigToAddNodeConfig } from './addNodeConfig'
import { resolveVisionCategory, resolveVisionType } from '@/config/pnidClassification'
import { TaxonomyProvider } from './TaxonomyContext'
import {
  disciplineFromProperties,
  hasDisciplineStamp,
  type DrawingDiscipline,
} from '@/config/classification'
import type { PnidLevelI } from '@/config/pnidClassification'
import { reviewStyleFor, buildReviewQueue, REVIEW_CONFIDENCE_THRESHOLD, type PnidLayer } from './reviewLayer'
import ReviewControls from './ReviewControls'
import { shouldDeleteActiveSelection } from './keyboardDelete'
import { toolChangePreservesSelection } from './toolChange'
import MultiSelectPanelV2, { type BulkClassification } from './MultiSelectPanelV2'
import { planBulkFieldUpdate, type BatchTextField } from './bulkField'

// ENG-2632: i18n key for each batch-editable field's human label (for the toast).
// Module-scope constant — it never changes, so it isn't re-created per render.
const FIELD_LABEL_KEY: Record<BatchTextField, string> = {
  tag: 'graph_editor:nodePanel.label',
  annotation_text: 'graph_editor:nodePanel.annotationText',
  note: 'graph_editor:nodePanel.note',
}
import type { AddNodeConfig } from './addNodeConfig'
import type { Edge, TableCellNode } from '@/service/graph'
import { v4 as uuidv4 } from 'uuid'
import { toast } from 'sonner'
import GraphWorker from './graphWorker?worker'
import { Loader2, FileBox, ChevronRight, Download, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import KnowledgeGraph from './KnowledgeGraph'
import CleanRedrawPreview from './cleanRedraw/CleanRedrawPreview'
import JsonViewer from './JsonViewer'
import OriginalDocumentViewer from './OriginalDocumentViewer'
import EditorLayersMenu from './EditorLayersMenu'
import { NO_LAYERS, type EditorLayers } from './editorLayers'
import { EDITOR_MODE, GRAPH_PREVIEW, JSON_VIEW, LINES_VIEW, ORIGINAL_VIEW } from '.'
import { LinesTab } from '@/components/DocumentViewer/LinesTab'
import { IO_LINE_TRACING_ENABLED } from '@/config/ioLineTracing'
import type {
  Node,
  SymbolNode,
  TagNode,
  ConnectionNode,
} from '@/service/graph'
import { parseLineStringSegments } from '@/service/graph'
import GraphToolbar from './GraphToolbar'
import GraphToolbarV2 from './GraphToolbarV2'
import { buildGoldDrawing } from './labelledExport'
import {
  GRAPH_SAVED_EVENT,
  type GraphSavedDetail,
  groundTruthFilename,
  isGraphSavedDetail,
  shouldStoreGroundTruth,
} from './groundTruthSnapshot'
import NodeDetailsPanelV2 from './NodeDetailsPanelV2'
import AddNodePanelV2 from './AddNodePanelV2'
import { useUIv2 } from '@/hooks/useUIv2'
import EditorSearch from './EditorSearch'
import { useNodeSearch } from './useNodeSearch'
import CommentPanel from '@/components/Comments/CommentPanel'
import { useTranslation } from '@/i18n'
import SheetEdgeNav from './SheetEdgeNav'
import SheetPageBar from './SheetPageBar'
import { cleanDocumentName } from '@/routes/IntelligentBoard/utils/stripGraphPrefix'

interface ProcessedGraphData {
  symbols: Record<string, SymbolNode[]>
  tags: TagNode[]
  connections: ConnectionNode[]
}


export function processGraphData(nodes: Node[]): ProcessedGraphData {
  // Initialize the result object with empty arrays
  const result: ProcessedGraphData = {
    symbols: {},
    tags: [],
    connections: [],
  }

  // Single pass through the nodes array
  for (const node of nodes) {
    switch (node.type) {
      case 'symbol': {
        // Group symbols by name (includes instruments which are now symbols)
        const symbolNode = node as SymbolNode
        if (!result.symbols[symbolNode.name]) {
          result.symbols[symbolNode.name] = []
        }
        result.symbols[symbolNode.name].push(symbolNode)
        break
      }
      case 'tag': {
        result.tags.push(node as TagNode)
        break
      }
      case 'connection': {
        result.connections.push(node as ConnectionNode)
        break
      }
    }
  }
  return result
}

interface EditorWrapperProps {
  graphId: string
  documentId?: string  // Optional: for P&ID pages within mixed documents
  initialZoom?: number
  isReadOnly?: boolean
  /**
   * Allow deleting diagram entities even when `isReadOnly` (FS-508).
   * Used by the in-board graph preview: the canvas stays read-only
   * (no drag/resize/add), but selecting an entity and clicking Remove
   * deletes it from local state and persists the deletion to the graph.
   */
  allowEntityDeletion?: boolean
  activeTab?: string
  /** JSON tab view mode: live annotation graph ('graph', default) vs GT export ('gt'). */
  jsonMode?: 'graph' | 'gt'
  onBoundingBoxStatusChange?: (hasBoundingBoxes: boolean) => void
  /**
   * Reports the drawing discipline once the graph document resolves.
   *
   * The graph-editor ROUTE renders panels this component does not own (the
   * labeling filter), so it needs the discipline to mount its own provider.
   * Reported from here rather than fetched again there: this component already
   * has the document, and the route deliberately costs no extra round trip.
   */
  onDisciplineResolved?: (discipline: DrawingDiscipline) => void
  /** When user clicks a drawing_to_drawing_connector, called with its label and extraProperties. Used for multi-page navigation. */
  onDrawingConnectorClick?: (label: string, extraProperties?: Record<string, string>) => void
  /** Navigate to a sheet by index (0-based). Used for edge nav buttons. */
  onNavigateToSheet?: (index: number) => void
  /** Current sheet index (0-based) within the multi-page set. */
  currentSheetIndex?: number
  /** Total number of sheets in the multi-page set. */
  totalSheets?: number
  /** Names of sheets in the multi-page set (ordered by index). */
  sheetNames?: string[]
  /**
   * Blocks the V2 canvas page bar while a sheet switch is in flight. The set
   * loader flips this on every switch with the PREVIOUS doc list still on
   * screen, so without it rapid clicks queue navigations against stale docs.
   */
  sheetNavDisabled?: boolean
  /** Optional sheet navigation element rendered in the breadcrumb header. */
  sheetNavigation?: React.ReactNode
  /** Real connector data for data-driven edge nav. */
  sheetConnectors?: import('@/types/sheet-connector').SheetConnector[]
  /** Multi-page documents list for resolving target names. */
  multiPageDocs?: { id: string; name: string }[]
  /** Callback when user clicks "+" to add a connector on an edge. */
  onAddConnector?: (edge: import('@/types/sheet-connector').SheetConnectorEdge) => void
  /** Callback when user clicks a connector button. */
  onConnectorClick?: (connector: import('@/types/sheet-connector').SheetConnector) => void
  /** Callback when user right-clicks a connector button. */
  onConnectorContextMenu?: (e: React.MouseEvent, connector: import('@/types/sheet-connector').SheetConnector) => void
  /** Page number (1-indexed) for multi-page PDF P&IDs — used to fetch the correct background image. */
  pnidPageNumber?: number
  /** External trigger to enter Add Object / Create mode (V2 sub-navbar). */
  enterCreateMode?: boolean
  /** Called when Create mode exits internally. */
  onCreateModeExit?: () => void
  /** Called when a node is selected (to coordinate right-side panel visibility). */
  onNodeSelected?: () => void
  /** External trigger to clear node selection (e.g. when Pending Review opens). */
  clearNodeSelection?: boolean
  /**
   * A node to land on once the drawing is built — selected and zoomed into
   * view, the same thing the `?highlightNode=` query param does, for a caller
   * that mounts this editor inside an overlay rather than on the graph-editor
   * route. Tag mapping's preview opens a sheet FOR a tag, so it hands over
   * the tag's node here. Unlike the URL param it does not open the node's
   * details panel.
   */
  highlightNodeId?: string | null
  /**
   * Draw none of the editor's own chrome — the tool rail, the Show Original /
   * layer switch and the zoom pill — for a host that draws its own. The
   * tag-mapping preview overlay: it has an Original tab, a Close and a zoom
   * bar of its own at the screen corners (Figma 13), and drives the canvas
   * through `zoomHandleRef`. The canvas, its selection and its details panel
   * are unchanged.
   */
  hideChrome?: boolean
  /** The canvas zoom actions, for a host's own zoom bar (see PnidRenderer). */
  zoomHandleRef?: React.MutableRefObject<PnidZoomHandle | null>
  /** The canvas zoom readout, for a host's own zoom bar. */
  onZoomChange?: (percent: number) => void
  /** When true, an external panel (review/history) is open — hide internal node/add panels to avoid stacking. */
  externalPanelOpen?: boolean
  /** Node IDs checked in the Pending Review panel — highlighted with an amber halo and framed into view (FS-523). */
  reviewHighlightNodeIds?: Set<string>
  /** FS-623 focus mode: dim non-matched shapes so the filter's matches stand alone. */
  dimUnmatchedHighlights?: boolean
  /** Called when the document name becomes available (for V2 breadcrumb). */
  onDocumentNameResolved?: (name: string) => void
  /** External search term driven by the V2 sub-navbar inline search. */
  externalSearchTerm?: string
  /**
   * Open the floating find box already searching for this (Tag Mapping's
   * preview seeds the row's tag). Only where the wrapper owns that box.
   */
  initialSearch?: string
  /** Callback to report current match info back to the sub-navbar. */
  onSearchMatchInfo?: (info: { total: number; current: number }) => void
  /** Callback for navigating to the next search match (Enter key in sub-navbar). */
  onSearchNext?: React.MutableRefObject<(() => void) | null>
  /** Callback for navigating to the previous search match (Shift+Enter in sub-navbar). */
  onSearchPrevious?: React.MutableRefObject<(() => void) | null>
  /**
   * The `table_index` whose cells to draw, or null for none. Table cells are normally
   * invisible: one table is hundreds of boxes, so always drawing them would swamp select-all,
   * bulk delete/classify and the filter counts. They show only while the Tables panel is open.
   */
  visibleTableIndex?: number | null
  /**
   * The cell with focus in the grid, painted in the highlight colour. Not routed through
   * `reviewHighlightNodeIds`: that path also frames its target, so the canvas would jump on
   * every Tab through a hundred-cell table.
   */
  focusedTableCellId?: string | null
  /**
   * A table cell clicked on the drawing — pushed back to the grid so it focuses there.
   * `null` when the same cell is clicked again, i.e. picked off: the grid drops its focus and
   * the canvas drops the spotlight, so the box stops looking selected.
   */
  onTableCellClick?: (nodeId: string | null) => void
  /**
   * The canvas multi-selection, reported so the Tables panel can offer merge/unmerge on it.
   * It has to travel through the route because the panel is EditorWrapper's sibling, not its
   * child, and the selection lives here (the renderer toggles it on a click in Select mode).
   */
  onSelectedCellIdsChange?: (nodeIds: string[]) => void
  /** True while the annotator is drawing the region for a new (unparsed) table. */
  drawTableRegion?: boolean
  /** The region they drew, in absolute image px. Consumed by the Tables panel. */
  onTableRegionDrawn?: (region: { x: number; y: number; width: number; height: number }) => void
}

interface TooltipData {
  nodeId: string
  x: number
  y: number
}

type Point2D = { x: number; y: number }

// Margin (image-px) beyond a placed junction dot within which a drawn line's endpoint is
// grabbed + linked when the junction is placed. Calibrated to Aramco drawing DPI — see
// docs/tenant-assumptions.md.
const JUNCTION_LINK_CATCH_PX = 14

const isAnnotation = (node: Node): boolean => node.pnid_level_i?.trim().toLowerCase() === 'annotation'

/** A symbol other than an Annotation box or a junction dot takes the lines ending at it. */
const takesLines = (node: Node): boolean => node.type === 'symbol' && node.name !== 'junction' && !isAnnotation(node)

/** A polygon edit that changed its shape, not one that only moved it. */
const reshaped = (before: Point2D[] | undefined, after: Point2D[] | undefined): boolean => {
  if (!before || !after || before.length < 3 || after.length < 3) return false
  if (before.length !== after.length) return true
  const dx = after[0].x - before[0].x
  const dy = after[0].y - before[0].y
  return after.some((p, i) => Math.abs(p.x - before[i].x - dx) > 0.5 || Math.abs(p.y - before[i].y - dy) > 0.5)
}

// If `point` lies inside `bbox`, snap it to the nearest perpendicular point
// on the bounding rectangle's perimeter. Keeps the user's clicked axis — if
// you click near the top edge you snap to the top, near the left edge you
// snap to the left — so the endpoint visibly docks on the boundary instead
// of floating inside the symbol. Points already outside/on the edge pass
// through unchanged.
function snapToBoundary(
  point: Point2D,
  bbox: { x?: number; y?: number; width?: number; height?: number },
): Point2D {
  if (bbox.x === undefined || bbox.y === undefined || !bbox.width || !bbox.height) return point
  const { x, y, width, height } = bbox
  const inside = point.x > x && point.x < x + width && point.y > y && point.y < y + height
  if (!inside) return point
  const distances = [
    { edge: 'left' as const, d: point.x - x, projected: { x, y: point.y } },
    { edge: 'right' as const, d: x + width - point.x, projected: { x: x + width, y: point.y } },
    { edge: 'top' as const, d: point.y - y, projected: { x: point.x, y } },
    { edge: 'bottom' as const, d: y + height - point.y, projected: { x: point.x, y: y + height } },
  ]
  distances.sort((a, b) => a.d - b.d)
  return distances[0].projected
}

// Project `curr` onto either a horizontal or vertical line relative to `prev`
// so each segment is clean 90°. Whichever axis has the larger delta wins.
function orthogonalSnap(prev: Point2D, curr: Point2D): Point2D {
  const dx = Math.abs(curr.x - prev.x)
  const dy = Math.abs(curr.y - prev.y)
  return dx >= dy ? { x: curr.x, y: prev.y } : { x: prev.x, y: curr.y }
}

// The point on `bbox`'s perimeter nearest to `p`. Used to keep a connected line's
// endpoint sitting on a symbol's boundary after the symbol is moved/resized: an
// endpoint now outside the box is pulled to the closest edge/corner; one still
// inside projects to the nearest edge (snapToBoundary).
function nearestBoundaryPoint(
  p: Point2D,
  bbox: { x: number; y: number; width: number; height: number },
): Point2D {
  const { x, y, width, height } = bbox
  const outside = p.x < x || p.x > x + width || p.y < y || p.y > y + height
  if (outside) {
    return {
      x: Math.max(x, Math.min(x + width, p.x)),
      y: Math.max(y, Math.min(y + height, p.y)),
    }
  }
  return snapToBoundary(p, bbox)
}


function EditorWrapper({
  activeTab = EDITOR_MODE,
  jsonMode = 'graph',
  graphId,
  documentId,
  initialZoom = 1,
  isReadOnly = false,
  allowEntityDeletion = false,
  onBoundingBoxStatusChange,
  onDisciplineResolved,
  onDrawingConnectorClick,
  onNavigateToSheet,
  currentSheetIndex = 0,
  totalSheets = 0,
  sheetNames,
  sheetNavDisabled = false,
  sheetNavigation,
  sheetConnectors,
  multiPageDocs,
  onAddConnector,
  onConnectorClick,
  onConnectorContextMenu,
  pnidPageNumber,
  enterCreateMode,
  onCreateModeExit,
  onNodeSelected,
  clearNodeSelection,
  highlightNodeId,
  hideChrome = false,
  zoomHandleRef,
  onZoomChange,
  externalPanelOpen,
  reviewHighlightNodeIds,
  dimUnmatchedHighlights,
  onDocumentNameResolved,
  externalSearchTerm,
  initialSearch,
  onSearchMatchInfo,
  onSearchNext,
  onSearchPrevious,
  visibleTableIndex = null,
  focusedTableCellId = null,
  onTableCellClick,
  onSelectedCellIdsChange,
  drawTableRegion = false,
  onTableRegionDrawn,
}: EditorWrapperProps) {
  const isV2 = useUIv2()
  // FS-657: on the full editor route the V2 sub-navbar owns search and feeds us
  // `externalSearchTerm`. The board card preview has no sub-navbar, so nothing
  // drove search there at all — in that case the wrapper owns the floating bar.
  const ownsFloatingSearch = !isV2 || externalSearchTerm === undefined
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [pnidId, setPnidId] = useState<string | undefined>(undefined)
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null)
  const [rectangles, setRectangles] = useState<Rectangle[]>([])
  const [activeTooltip, setActiveTooltip] = useState<TooltipData | null>(null)

  // Local state for nodes and edges when in read-only mode (boards)
  // This prevents multiple P&IDs from sharing the same global store state
  const [localNodes, setLocalNodes] = useState<Map<string, Node>>(new Map())
  const [localEdges, setLocalEdges] = useState<Map<string, Edge>>(new Map())
  const [addNodeConfig, setAddNodeConfig] = useState<AddNodeConfig>({
    type: NodeType.Symbol,
  })
  // ENG-2631: persist the Add-Object panel's shape + classification across close/
  // reopen. The panel remounts each time it reopens and would otherwise reset to
  // empty — so after placing an object, clicking the Details panel's X and coming
  // back to Add Object used to lose the selection. The panel initializes from this
  // draft, keeping the shape + Category/Subcategory/Type sticky for the next object.
  const [addObjectDraft, setAddObjectDraft] = useState<{
    category: string; subcategory: string; type: string; label: string; note: string
    annotationText: string; shape: 'rectangle' | 'polygon' | 'line' | 'point'
  }>({ category: '', subcategory: '', type: '', label: '', note: '', annotationText: '', shape: 'rectangle' })
  const [mode, setMode] = useState<ModeType>(ModeType.Drag)
  // When the user switches to hand/pan while the Add Node panel is open,
  // we flip to Drag for panning but keep this flag so the panel stays visible.
  const [panningFromCreate, setPanningFromCreate] = useState(false)
  const [connectionMarkers, setConnectionMarkers] = useState<{
    connectedPoints: Array<{ x: number; y: number; markerId: string; symbolId: string; lineId: string }>
    danglingPoints: Array<{ x: number; y: number; markerId: string; lineId: string }>
    candidatePoints: Array<{ x: number; y: number; lineId: string; symbolId: string | null }>
  } | null>(null)
  // Context captured at the moment a manual symbol is detected — used to rebuild
  // the pending patch when the user drags markers to correct connections.
  const manualSymbolContextRef = useRef<{
    newNodeId: string
    bbox: { x: number; y: number; width: number; height: number }
    config: AddNodeConfig
    nodeType: 'symbol' | 'tag'
    tagIds: string[]
  } | null>(null)
  const [detectedBoundary, setDetectedBoundary] = useState<{
    x: number; y: number; width: number; height: number
  } | null>(null)
  const [pendingGraphPatch, setPendingGraphPatch] = useState<{
    patch: Parameters<typeof apiService.updateGraph>[1]
    connectedCount: number
    danglingCount: number
  } | null>(null)

  // External trigger to enter Create mode (from V2 sub-navbar "+ Add Object")
  useEffect(() => {
    if (enterCreateMode && mode !== ModeType.Create) {
      setMode(ModeType.Create)
      setActiveTooltip(null)
      setHighlightedNodeId(null)
    } else if (!enterCreateMode && mode === ModeType.Create) {
      setMode(ModeType.Drag)
    }
  }, [enterCreateMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Drawing a table region reuses the rectangle-drag tool, so Create mode has to be on;
  // `handleAdd` diverts the drawn box instead of creating a node while this is set.
  //
  // The shape reset is load-bearing: `addNodeConfig.shape` is sticky across Add-Object
  // sessions and is what puts the renderer into polygon/point/line mode. Left on 'line',
  // "New table" would quietly draw a connection and never emit a rectangle.
  useEffect(() => {
    if (drawTableRegion) {
      setAddNodeConfig((prev) => ({ ...prev, shape: 'rectangle' }))
      setMode(ModeType.Create)
      setActiveTooltip(null)
      setHighlightedNodeId(null)
    } else if (!enterCreateMode && mode === ModeType.Create) {
      setMode(ModeType.Drag)
    }
  }, [drawTableRegion]) // eslint-disable-line react-hooks/exhaustive-deps

  // External trigger to clear node selection (e.g. when Pending Review opens)
  useEffect(() => {
    if (clearNodeSelection) {
      setActiveTooltip(null)
      setHighlightedNodeId(null)
    }
  }, [clearNodeSelection])

  // Notify parent when a node is selected
  useEffect(() => {
    if (activeTooltip) {
      onNodeSelected?.()
    }
  }, [activeTooltip]) // eslint-disable-line react-hooks/exhaustive-deps

  // parsedGraph value is unread — only the setter is consumed by graph-worker handlers.
  const [_parsedGraph, setParsedGraph] = useState<{
    nodes: Node[]
    edges: { id: string; fromNodeId: string; toNodeId: string }[]
  } | null>(null)
  const [graphDocument, setGraphDocument] = useState<DocumentRecord | null>(
    null
  )
  const [pnidDocument, setPnidDocument] = useState<DocumentRecord | null>(
    null
  )
  const [loading, setLoading] = useState(true)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  // FS-621: bump to re-run the initial load (e.g. when the readiness poll sees
  // the graph become ready, or after a retry).
  const [reloadKey, setReloadKey] = useState(0)
  const [retrying, setRetrying] = useState(false)

  // Notify parent of resolved document name for breadcrumb
  useEffect(() => {
    const name = pnidDocument?.name || graphDocument?.name
    if (name && onDocumentNameResolved) onDocumentNameResolved(name)
  }, [pnidDocument?.name, graphDocument?.name, onDocumentNameResolved])
  const [isFromReviewFlow, setIsFromReviewFlow] = useState(false)
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(
    null
  )
  const [connectionStartPointAbs, setConnectionStartPointAbs] = useState<{ x: number; y: number } | null>(null)
  const [connectionStartNodeId, setConnectionStartNodeId] = useState<string | null>(null)
  // Intermediate polyline vertices between the start symbol and the end symbol.
  // Each canvas click while in connection mode adds one vertex, enabling
  // multi-segment (Paint/Visio-style) connection routing.
  const [connectionVertices, setConnectionVertices] = useState<Array<{ x: number; y: number }>>([])
  // The in-progress line is committed incrementally — created on the
  // 2nd point and updated on every subsequent click — so the line is a real
  // (solid, persisted) connection as you draw, not a preview held until you
  // close a loop. This holds that growing connection node's id (null = no line
  // committed yet, i.e. only the start point placed).
  const [connectionLineId, setConnectionLineId] = useState<string | null>(null)
  // Redo stack for line-segment peeling: each undo of a finished line's segment
  // pushes the pre-peel connection node (plus any edges removed when the final
  // segment is peeled) so redo can fully re-add it. Cleared on a new draw.
  const [lineRedoStack, setLineRedoStack] = useState<Array<{ node: ConnectionNode; edges: Edge[] }>>([])
  // True when the most-recent undoable action was a snapshot (delete / add / move),
  // so Undo restores that snapshot instead of peeling a still-selected line. Reset
  // to false whenever a fresh line-draw action happens (peel becomes the natural Undo).
  const lastActionWasSnapshotRef = useRef(false)
  // Symbol id currently under the cursor during connection drawing — drives the
  // hover halo so the user can see which symbol the connection will drop onto.
  const [connectionHoveredId, setConnectionHoveredId] = useState<string | null>(null)
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set())

  // Report the selected TABLE CELLS upward for the Tables panel's merge/unmerge actions.
  // Filtered here because this is where the nodes are: the panel would otherwise have to
  // decide what counts as a cell from ids alone.
  //
  // Reports ONLY on a real change. This effect runs on every canvas selection change, and
  // without the guard it pushed a fresh empty array on every symbol/tag selection with the
  // Tables grid shut — re-rendering the editor route for work that has nothing to do with
  // tables. The rest of the P&ID HITL surface must not pay for this feature.
  const reportedCellIdsRef = useRef('')
  useEffect(() => {
    if (!onSelectedCellIdsChange) return
    const cells =
      visibleTableIndex === null
        ? []
        : Array.from(selectedNodeIds).filter((id) => activeNodes.get(id)?.type === 'table_cell')
    const key = cells.join(',')
    if (key === reportedCellIdsRef.current) return
    reportedCellIdsRef.current = key
    onSelectedCellIdsChange(cells)
    // activeNodes is intentionally out of the deps: it changes on every geometry edit, and a
    // merge rewrites geometry — re-running then would report the same ids in a loop.
  }, [selectedNodeIds, visibleTableIndex, onSelectedCellIdsChange]) // eslint-disable-line react-hooks/exhaustive-deps

  const [isEditorSearchOpen, setIsEditorSearchOpen] = useState(false)
  useEffect(() => {
    if (initialSearch?.trim() && ownsFloatingSearch) setIsEditorSearchOpen(true)
  }, [initialSearch, ownsFloatingSearch])
  // FS-657: host for the search match list. It docks beside the canvas (a flex
  // sibling, like NodeDetailsPanelV2) instead of floating over the drawing,
  // which used to cover the very object you'd just searched for.
  const [searchListHost, setSearchListHost] = useState<HTMLDivElement | null>(null)
  // HITL review layer switcher (PRD "Vision HITL Labeling"): Original | Detected | Review.
  // `showOriginal` is derived so the download + overlay logic below is unchanged.
  const [pnidLayer, setPnidLayer] = useState<PnidLayer>('detected')
  const showOriginal = pnidLayer === 'original'
  const isReviewLayer = pnidLayer === 'review'
  // VISN-360: read-only overlays (flow direction, label links by rule, pipe line numbers); all off by default.
  const [editorLayers, setEditorLayers] = useState<EditorLayers>(NO_LAYERS)
  const [reviewThreshold, setReviewThreshold] = useState(REVIEW_CONFIDENCE_THRESHOLD)
  const [reviewFullColor, setReviewFullColor] = useState(false)
  // VISN-291: symbol-classification review by default; text (PyMuPDF-deterministic
  // at ~97% conf=1.0 on this pipeline) reviewed only when explicitly toggled in.
  const [reviewSymbolsOnly, setReviewSymbolsOnly] = useState(true)
  const [isCommentPanelOpen, setIsCommentPanelOpen] = useState(false)
  const [searchMatchNodeIds, setSearchMatchNodeIds] = useState<Set<string>>(new Set())

  // Highlight a node from URL query param (e.g. when navigating from a cross-sheet connector)
  const highlightNodeParam = searchParams.get('highlightNode')
  // Separate state for cross-sheet highlight — not touched by any click/mode handlers
  const [crossSheetHighlightId, setCrossSheetHighlightId] = useState<string | null>(null)

  const [processedGraphData, setProcessedGraphData] =
    useState<ProcessedGraphData>({
      symbols: {},
      tags: [],
      connections: [],
    })

  const [hasBoundingBoxes, setHasBoundingBoxes] = useState(true)

  // Ref for PnidRenderer container to enable SVG export
  const pnidContainerRef = useRef<HTMLDivElement>(null)

  // Track blob URL for background image to revoke on cleanup
  const backgroundImageUrlRef = useRef<string | null>(null)

  const {
    graph,
    hydrateState,
    currentState,
    versionHistory,
    getNodeById,
    updateNode,
    updateMultipleNodes,
    addNodes,
    addEdge,
    deleteNodeById,
    deleteMultipleNodes,
    deleteEdgeById,
    undo,
    redo,
    undoPast,
    undoFuture,
  } = useAppStore()

  // Use local state in read-only mode (boards), global store in edit mode
  const activeNodes = isReadOnly ? localNodes : currentState.nodes
  const activeEdges = isReadOnly ? localEdges : currentState.edges
  // Graph Preview layout: the original cards, Clean redraw (the P&ID redrawn from the graph), or Line map.
  // It opens on Cards every time. The switch lasts for this view only: a look at Clean redraw on one
  // graph is not remembered, so the next graph (or the next visit) opens on Cards again.
  const [previewView, setPreviewView] = useState<'clean' | 'lines' | 'cards'>('cards')

  // Create wrapper functions that work with active state instead of global store
  const getActiveNodeById = (nodeId: string): Node => {
    if (isReadOnly) {
      const node = activeNodes.get(nodeId)
      if (!node) throw new Error(`Node ${nodeId} not found`)
      return node
    }
    return getNodeById(nodeId)
  }

  const updateActiveNode = (node: Node): Node => {
    if (isReadOnly) {
      // Read-only mode: mutations are not allowed
      console.warn('Cannot update node in read-only mode')
      return node
    }
    const before = useAppStore.getState().currentState.nodes.get(node.id as string)
    const updated = updateNode(node)
    // Anything turned into an Annotation gives back the links it was given automatically, and a
    // symbol turned back from one picks its lines up again (see attachLinesToSymbol).
    if (before && !isAnnotation(before) && isAnnotation(node)) dropAutoLinks(node.id as string)
    else if (before && !takesLines(before) && takesLines(node)) attachLinesToSymbol(node.id as string)
    return updated
  }

  const deleteActiveNodeById = (nodeId: string): void => {
    // FS-508: the in-board preview is read-only for canvas editing but
    // allows entity deletion. Previews render from local state (to avoid
    // cross-contaminating the shared graph store across multiple open
    // previews), so delete from localNodes/localEdges and persist the
    // deletion to the backend. Optimistic with revert on failure.
    if (isReadOnly && allowEntityDeletion) {
      const connectedEdgeIds = collectConnectedEdgeIds(nodeId, localEdges)

      const prevNodes = localNodes
      const prevEdges = localEdges

      setLocalNodes((prev) => {
        const next = new Map(prev)
        next.delete(nodeId)
        return next
      })
      if (connectedEdgeIds.length > 0) {
        setLocalEdges((prev) => {
          const next = new Map(prev)
          connectedEdgeIds.forEach((edgeId) => next.delete(edgeId))
          return next
        })
      }

      apiService
        .updateGraph(graphId, buildEntityDeletePatch(nodeId, connectedEdgeIds))
        .then(() => {
          toast.success(t('data_catalog:graph_editor.toasts.entity_deleted'))
        })
        .catch((error) => {
          console.error('Failed to delete entity from graph:', error)
          toast.error(t('data_catalog:graph_editor.toasts.entity_delete_failed'))
          // Revert THIS deletion only, via functional updates, so a
          // concurrent successful delete isn't clobbered by a blanket
          // snapshot restore. Re-insert the node + its edges we removed.
          const deletedNode = prevNodes.get(nodeId)
          if (deletedNode) {
            setLocalNodes((cur) =>
              cur.has(nodeId) ? cur : new Map(cur).set(nodeId, deletedNode),
            )
          }
          if (connectedEdgeIds.length > 0) {
            setLocalEdges((cur) => {
              const next = new Map(cur)
              let changed = false
              connectedEdgeIds.forEach((edgeId) => {
                const edge = prevEdges.get(edgeId)
                if (edge && !next.has(edgeId)) {
                  next.set(edgeId, edge)
                  changed = true
                }
              })
              return changed ? next : cur
            })
          }
        })
      return
    }

    if (isReadOnly) {
      // Read-only mode: mutations are not allowed
      console.warn('Cannot delete node in read-only mode')
      return
    }
    deleteNodeById(nodeId)
    removeConnectors([nodeId])
    setConnectionMarkers(null)
    setPendingGraphPatch(null)
    setDetectedBoundary(null)
  }

  const deleteActiveEdgeById = (edgeId: string): void => {
    if (isReadOnly) {
      // Read-only mode: mutations are not allowed
      console.warn('Cannot delete edge in read-only mode')
      return
    }
    deleteEdgeById(edgeId)
  }

  const addActiveEdge = (edge: Edge): Edge => {
    if (isReadOnly) {
      // Read-only mode: mutations are not allowed
      console.warn('Cannot add edge in read-only mode')
      return edge
    }
    return addEdge(edge)
  }

  // A drawn symbol picks up the lines whose free ends sit at it, and a drawn line the symbols at
  // its free ends (lineAttach.ts). Links run line → symbol, the way a drawn line links its ends.
  // Classifying a box as Annotation afterwards removes the links added here, so a framing box
  // drawn before it is classified does not keep the line ends along its border.
  const autoLinkIds = useRef(new Set<string>())
  const addAutoLink = (lineId: string, symbolId: string): void => {
    const id = uuidv4()
    autoLinkIds.current.add(id)
    addActiveEdge({ id, fromNodeId: lineId, toNodeId: symbolId })
  }
  // symbol id → the connectors drawn from junctions on its outline; they go with the symbol.
  const connectorsOf = useRef(new Map<string, string[]>())
  const removeConnectors = (symbolIds: string[]): void => {
    const live = useAppStore.getState().currentState.nodes
    const ids = symbolIds.flatMap((id) => connectorsOf.current.get(id) ?? []).filter((id) => live.has(id))
    for (const id of symbolIds) connectorsOf.current.delete(id)
    if (ids.length > 0) deleteMultipleNodes(ids)
  }
  const dropAutoLinks = (nodeId: string): void => {
    for (const edge of useAppStore.getState().currentState.edges.values()) {
      if (!edge.id || !autoLinkIds.current.has(edge.id)) continue
      if (edge.fromNodeId !== nodeId && edge.toNodeId !== nodeId) continue
      deleteActiveEdgeById(edge.id)
      autoLinkIds.current.delete(edge.id)
    }
    removeConnectors([nodeId])
  }

  const attachLinesToSymbol = (symbolId: string): void => {
    const { nodes, edges } = useAppStore.getState().currentState
    const lineIds = linesEndingAtSymbol(symbolId, nodes, edges)
    for (const lineId of lineIds) addAutoLink(lineId, symbolId)
    // A junction on the outline (the parse's mark where a line meets a vessel wall) gets a short
    // line to the symbol. `carried_to` names the symbol, so the save drops the line once the
    // symbol is gone, as it does for the lines the link carry draws.
    // Read after the links above, so a tee whose line already reaches the symbol gets none.
    const now = useAppStore.getState().currentState
    const connectors = junctionConnectors(symbolId, now.nodes, now.edges)
    for (const c of connectors) {
      const from = now.nodes.get(c.classFrom)
      const id = uuidv4()
      useAppStore.getState().addNode({
        id,
        type: 'connection',
        geometry_wkt: c.geometryWkt,
        pnid_level_i: from?.pnid_level_i,
        pnid_level_ii: from?.pnid_level_ii,
        pnid_level_iii: from?.pnid_level_iii,
        source: 'manual',
        extraProperties: { carried_to: symbolId },
      } as ConnectionNode)
      addAutoLink(id, c.junction)
      addAutoLink(id, symbolId)
      connectorsOf.current.set(symbolId, [...(connectorsOf.current.get(symbolId) ?? []), id])
    }
    const linked = lineIds.length + connectors.length
    if (linked > 0) {
      toast.success(t('graph_editor:editor.symbolLinked', { count: linked, defaultValue: 'Linked to {{count}} line(s)' }))
    }
  }

  const attachSymbolsToLine = (lineId: string): void => {
    const { nodes, edges } = useAppStore.getState().currentState
    for (const symbolId of symbolsAtLineEnds(lineId, nodes, edges)) addAutoLink(lineId, symbolId)
  }

  const deleteActiveMultipleNodes = (nodeIds: string[]): void => {
    if (isReadOnly) {
      // Read-only mode: mutations are not allowed
      console.warn('Cannot delete nodes in read-only mode')
      return
    }
    deleteMultipleNodes(nodeIds)
    removeConnectors(nodeIds)
  }

  // Create active state object that matches the structure expected by DetailsFlyout
  const activeState = {
    nodes: activeNodes,
    edges: activeEdges,
    isModified: isReadOnly ? false : currentState.isModified,
  }

  // ENG-2595: latest loaded background-image size — the pixel space node geometry lives in,
  // used as image_size and the normalization basis for the JSON tab's GT preview.
  const [exportImageSize, setExportImageSize] = useState<{ width: number; height: number } | null>(null)

  // Trigger a browser download of `blob` as `fileName`. Single source for the
  // anchor dance so the annotated and Show-Original paths stay in sync.
  const triggerDownload = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.download = fileName
    link.href = url
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    setTimeout(() => URL.revokeObjectURL(url), 100)
  }

  // Download the P&ID image, matching whatever the editor is currently showing:
  // with "Show Original" active the user is looking at the unannotated original,
  // so download that as-is; otherwise download the annotated editor view
  // (processed background + parsed bounding boxes). Shares renderAnnotatedPnidBlob
  // with the Data Catalog row download so both produce the identical image (FS-622).
  const downloadPnidImage = async () => {
    if (showOriginal) {
      if (!pnidId) {
        toast.error(t('data_catalog:graph_editor.toasts.pnid_not_ready_download'))
        return
      }
      try {
        // Mirror the OriginalDocumentViewer page resolution so the download
        // matches the page the overlay is showing.
        const page = documentId ? parseInt(searchParams.get('page') || '1', 10) : undefined
        const blob = await apiService.downloadPnidImage(
          pnidId,
          page != null ? { type: 'original', page } : { type: 'original' },
        )
        const ext = blob.type === 'application/pdf' ? 'pdf' : blob.type === 'image/jpeg' ? 'jpg' : 'png'
        const baseName = pnidDocument?.name ? stripFileExtension(pnidDocument.name) : null
        const fileName = baseName
          ? `${baseName}.${ext}`
          : `pnid-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)}.${ext}`
        triggerDownload(blob, fileName)
        trackEvent('parse-result_exported', {
          document_id: graphId,
          document_type: 'pnid',
          export_format: ext,
        })
        toast.success(t('data_catalog:graph_editor.toasts.pnid_image_downloaded'))
      } catch {
        toast.error(t('data_catalog:graph_editor.toasts.pnid_load_failed'))
      }
      return
    }

    const imageBlobUrl = backgroundImageUrlRef.current
    if (!imageBlobUrl) {
      toast.error(t('data_catalog:graph_editor.toasts.pnid_not_ready_download'))
      return
    }

    try {
      const blob = await renderAnnotatedPnidBlob(imageBlobUrl, rectangles)
      const baseName = pnidDocument?.name ? stripFileExtension(pnidDocument.name) : null
      const fileName = baseName
        ? `${baseName}.png`
        : `pnid-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)}.png`
      triggerDownload(blob, fileName)
      trackEvent('parse-result_exported', {
        document_id: graphId,
        document_type: 'pnid',
        export_format: 'png',
      })
      toast.success(t('data_catalog:graph_editor.toasts.pnid_image_downloaded'))
    } catch (err) {
      const message = (err as Error).message
      const key =
        message === PNID_RENDER_ERROR.canvasContext
          ? 'data_catalog:graph_editor.toasts.canvas_context_failed'
          : message === PNID_RENDER_ERROR.createImage
            ? 'data_catalog:graph_editor.toasts.create_image_failed'
            : 'data_catalog:graph_editor.toasts.pnid_load_failed'
      toast.error(t(key))
    }
  }

  // FS-622: keep a live ref to downloadPnidImage so the `graph-download` window
  // listener (registered below, keyed only on activeTab) always invokes the
  // latest closure. Without it the listener captured the very first render —
  // when `rectangles` is still [] and `pnidDocument` is null — so the V2
  // sub-navbar Download produced a blank, unannotated image named
  // `pnid-<timestamp>.png`. Mirrors downloadLabelledJsonRef (ENG-2595).
  const downloadPnidImageRef = useRef(downloadPnidImage)
  downloadPnidImageRef.current = downloadPnidImage

  // VISN-319: the JSON tab's PARSED mode shows the FROZEN machine parse — patch
  // version 1 of the graph, i.e. what the parser wrote before any human edit —
  // fetched from the backend once per graph. It must NOT re-serialize the live
  // editor state: after an annotation save the live graph IS the human-edited
  // state, so exporting it as "PARSED" made the PARSED and Ground Truth downloads
  // near-identical and destroyed the very separation this toggle exists to show.
  // (The old re-serialization was also lossy: fresh property UUIDs every render,
  // and it dropped pnid_level_*, annotation_text, table-cell fields and polygon
  // vertices.)
  const [parsedV1, setParsedV1] = useState<{ graphId: string; data: unknown; ok: boolean } | null>(null)
  useEffect(() => {
    if (activeTab !== JSON_VIEW || jsonMode !== 'graph' || !graphId) return
    if (parsedV1?.graphId === graphId) return
    let cancelled = false
    apiService
      .getGraphData(graphId, { version: 1 })
      .then((data) => {
        if (!cancelled) setParsedV1({ graphId, data, ok: true })
      })
      .catch(() => {
        // Sticky per graph — no auto-retry loop; reopening the editor refetches.
        if (!cancelled) {
          setParsedV1({
            graphId,
            data: {
              note: t('graph_editor:jsonViewer.parsedLoadFailed', {
                defaultValue: 'Failed to load the parsed JSON (version 1 of this graph). Close and reopen the editor to retry.',
              }),
            },
            ok: false,
          })
        }
      })
    return () => {
      cancelled = true
    }
  }, [activeTab, jsonMode, graphId, parsedV1, t])

  // Present the frozen parse in the EXACT display structure this tab has always
  // had (document_info first, `nodes` serialized as {} — the legacy Set
  // serialization — then edges and properties). VISN-319 changes only WHERE the
  // data comes from (the frozen v1 parse instead of the live editor state), not
  // the JSON shape; restructuring is the ticket's separate goal #2.
  const parsedGraphData: unknown = useMemo(() => {
    if (parsedV1?.graphId !== graphId) {
      return {
        note: t('graph_editor:jsonViewer.parsedLoadingNote', {
          defaultValue: 'Loading the parsed JSON (the original machine parse)…',
        }),
      }
    }
    if (!parsedV1.ok) return parsedV1.data
    const v1 = parsedV1.data as {
      id?: string
      edges?: Record<string, unknown>
      properties?: Record<string, unknown>
    }
    return {
      ...(graph.document_info ? { document_info: graph.document_info } : {}),
      id: v1.id ?? graphId,
      nodes: {},
      edges: v1.edges ?? {},
      properties: v1.properties ?? {},
    }
  }, [parsedV1, graphId, graph.document_info, t])

  // VISN-319: ground truth only exists once a human has labelled. Patch version 1
  // is the machine parse, so any saved version above 1 means a human (or a carried
  // re-parse overlay) edited the graph; unsaved local edits count too.
  //
  // The version info is tracked LOCALLY, keyed by graphId — deliberately NOT read
  // straight off the store's `versionHistory`, which is global and never reset on
  // navigation: after one save anywhere, its stale "latest version 2" leaked into
  // every sheet opened afterwards and made unlabelled sheets show a ground truth.
  const [gtVersionInfo, setGtVersionInfo] = useState<{ graphId: string; latestVersion: number } | null>(null)
  useEffect(() => {
    if (activeTab !== JSON_VIEW || jsonMode !== 'gt' || !graphId || isReadOnly) return
    if (gtVersionInfo?.graphId === graphId) return
    apiService
      .getVersionHistory(graphId)
      .then((history) => {
        const latest = history.reduce((max, v) => Math.max(max, v.version), 0)
        setGtVersionInfo({ graphId, latestVersion: latest })
      })
      .catch(() => {
        /* stays unknown; the GT pane keeps its checking note */
      })
  }, [activeTab, jsonMode, graphId, isReadOnly, gtVersionInfo])

  // A save (publishGraph) or a Version History panel load refreshes the store's
  // versionHistory, and those always concern the CURRENTLY-open graph — mirror
  // such changes into the graph-keyed copy so GT appears right after the first
  // save. The ref-compare skips the mount run, so a stale store value from a
  // previously-open sheet is never attributed to this one.
  const prevVersionsRef = useRef(versionHistory.versions)
  useEffect(() => {
    if (prevVersionsRef.current === versionHistory.versions) return
    prevVersionsRef.current = versionHistory.versions
    if (!graphId || versionHistory.versions.length === 0) return
    const latest = versionHistory.versions.reduce((max, v) => Math.max(max, v.version), 0)
    setGtVersionInfo({ graphId, latestVersion: latest })
  }, [versionHistory.versions, graphId])

  const latestSavedVersion = gtVersionInfo?.graphId === graphId ? gtVersionInfo.latestVersion : undefined

  // The STORED ground truth for this graph's page — the answer key a re-parse cannot
  // move. The Ground Truth tab shows it whenever one exists, so what is on screen is
  // what Download hands out. Keyed by graphId like the version info, for the same
  // reason. `snapshot: null` = checked, nothing stored; `failed` = could not check.
  const [storedGt, setStoredGt] = useState<{
    graphId: string
    snapshot: LatestGroundTruthResult['snapshot']
    failed?: boolean
  } | null>(null)
  useEffect(() => {
    if (activeTab !== JSON_VIEW || jsonMode !== 'gt' || !graphId) return
    if (storedGt?.graphId === graphId) return
    let cancelled = false
    apiService
      .getLatestGroundTruthSnapshot(graphId)
      .then(({ snapshot }) => {
        if (!cancelled) setStoredGt({ graphId, snapshot })
      })
      .catch((err) => {
        console.warn('Could not load the stored ground truth:', err)
        if (!cancelled) setStoredGt({ graphId, snapshot: null, failed: true })
      })
    return () => {
      cancelled = true
    }
  }, [activeTab, jsonMode, graphId, storedGt])

  // The sheet page this graph labels (1 for a single-page drawing). The GT export
  // and the stored snapshot both carry it, and the server refuses a snapshot whose
  // page is not the page the graph is mapped to — so it must be the real one.
  const currentSheetPage = pnidPageNumber ?? (documentId ? parseInt(searchParams.get('page') || '1', 10) : 1)

  // ENG-2595: the JSON tab's "Ground Truth" mode. With no unsaved edits it shows the STORED
  // ground truth when one exists — the copy Download hands out — so a re-parse, which
  // replaces the live graph, cannot change what the tab shows. Unsaved edits, and sheets
  // with no stored row, get the live preview: the exact buildGoldDrawing the Export-GT
  // button uses, over the live store. Placeholder until the page image size is known.
  const goldDrawingData = useMemo<unknown>(() => {
    if (jsonMode !== 'gt') return null
    // Only build GT while the JSON tab is actually showing — otherwise an edit on the Editor
    // tab (with 'gt' still selected) would re-run buildGoldDrawing on every node/edge mutation.
    if (activeTab !== JSON_VIEW) return null
    // VISN-319: without a single human edit there IS no ground truth — say so
    // instead of fabricating a "GT" that is just the parse re-formatted.
    const hasUnsavedEdits = !isReadOnly && currentState.isModified
    if (!hasUnsavedEdits) {
      const stored = storedGt?.graphId === graphId ? storedGt : undefined
      if (stored === undefined) {
        return { note: t('graph_editor:jsonViewer.gtCheckingNote', { defaultValue: 'Checking for human labels…' }) }
      }
      if (stored.snapshot) {
        return {
          note: t('graph_editor:jsonViewer.gtStoredNote', {
            defaultValue:
              'Stored ground truth, saved {{when}} (graph version {{version}}). This is the copy Download hands out; a re-parse cannot change it.',
            when: new Date(stored.snapshot.created_at).toLocaleString(),
            version: stored.snapshot.graph_version,
          }),
          ...stored.snapshot.payload,
        }
      }
      if (stored.failed) {
        return {
          note: t('graph_editor:jsonViewer.gtStoredLoadFailed', {
            defaultValue: 'Could not check for a stored ground truth. Reopen the tab to try again.',
          }),
        }
      }
      if (latestSavedVersion === undefined) {
        return { note: t('graph_editor:jsonViewer.gtCheckingNote', { defaultValue: 'Checking for human labels…' }) }
      }
      if (latestSavedVersion <= 1) {
        return {
          ground_truth: null,
          note: t('graph_editor:jsonViewer.gtAbsentNote', {
            defaultValue:
              'No ground truth yet — no human has labelled this drawing. Ground truth is created by editing in the Editor tab and saving.',
          }),
        }
      }
    }
    if (!exportImageSize) {
      return { note: t('graph_editor:jsonViewer.gtLoadingNote', { defaultValue: 'Ground-truth preview will populate once the drawing finishes loading.' }) }
    }
    const nodes = Array.from(activeNodes.values())
    const edges = Array.from(activeEdges.values())
    // Read the doc name from its reactive source so the preview's drawing_key isn't stuck on the
    // graph UUID if the name resolves after first paint (the memo re-runs when these deps change).
    const rawName = pnidDocument?.name || graphDocument?.name || graphId
    const drawingKey = rawName.replace(/\.[^./]+$/, '')
    return buildGoldDrawing(nodes, edges, exportImageSize, { drawingKey, page: currentSheetPage })
  }, [jsonMode, activeTab, activeNodes, activeEdges, exportImageSize, graphId, pnidDocument?.name, graphDocument?.name, latestSavedVersion, storedGt, isReadOnly, currentState.isModified, currentSheetPage, t])

  // The stored ground truth follows the human save. `publishGraph` fires GRAPH_SAVED_EVENT
  // once the save has landed; this freezes the GoldDrawing for that version so the answer
  // key lives where a re-parse cannot reach it (a re-parse replaces the graph; it cannot
  // update or delete a stored snapshot). Live refs: the listener is registered once and
  // must read the current graph, page size and name. Best-effort and NON-BLOCKING — the
  // graph is already saved, so a failure here must never fail the save.
  const storeGroundTruthRef = useRef<() => void>(() => {})
  storeGroundTruthRef.current = () => {
    if (!graphId || !exportImageSize) return
    // Read the store, not this render's `activeNodes`: the event fires right after
    // `hydrateState`, before React has re-rendered, so the render-time maps are the
    // pre-hydration draft. The store already holds the saved graph.
    const { nodes: savedNodes, edges: savedEdges } = useAppStore.getState().currentState
    // The name, like the graph id, comes through a live ref: the listener is
    // registered once, and the document can resolve after mount.
    const rawName = pnidDocumentRef.current?.name || graphDocumentRef.current?.name || graphId
    const payload = buildGoldDrawing(Array.from(savedNodes.values()), Array.from(savedEdges.values()), exportImageSize, {
      drawingKey: rawName.replace(/\.[^./]+$/, ''),
      page: currentSheetPage,
    })
    void apiService
      .recordGroundTruthSnapshot(graphId, payload)
      .then((res) => {
        // A skip is legitimate (unchanged bytes, not a P&ID graph) but worth seeing
        // when asking "why was no ground truth stored for this save?".
        if (res?.skipped) console.info('Ground-truth snapshot skipped:', res.reason)
        // The Ground Truth tab shows the stored copy: hand it the row just written
        // (its payload is the one posted) rather than re-fetching. Keyed by the id
        // captured above, so a store that lands after navigation is ignored.
        else if (res?.snapshot) setStoredGt({ graphId, snapshot: { ...res.snapshot, payload: { ...payload } } })
      })
      .catch((err) => {
        console.warn('Ground-truth snapshot failed (non-fatal):', err)
      })
  }
  const graphIdRef = useRef(graphId)
  graphIdRef.current = graphId
  const pnidDocumentRef = useRef(pnidDocument)
  pnidDocumentRef.current = pnidDocument
  const graphDocumentRef = useRef(graphDocument)
  graphDocumentRef.current = graphDocument
  // A save that lands before the page image has reported its size cannot be
  // exported yet (geometry is normalized by that size). Park it and store once the
  // size arrives, rather than dropping a successful save on the floor.
  const pendingSaveRef = useRef<GraphSavedDetail | null>(null)
  useEffect(() => {
    const onSaved = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail
      if (!isGraphSavedDetail(detail)) return
      if (!shouldStoreGroundTruth(detail, graphIdRef.current, exportImageSize)) {
        pendingSaveRef.current =
          detail.graphId === graphIdRef.current && detail.version > 1 && !exportImageSize ? detail : null
        return
      }
      pendingSaveRef.current = null
      storeGroundTruthRef.current()
    }
    window.addEventListener(GRAPH_SAVED_EVENT, onSaved)
    return () => window.removeEventListener(GRAPH_SAVED_EVENT, onSaved)
  }, [exportImageSize])
  useEffect(() => {
    const pending = pendingSaveRef.current
    if (!pending) return
    // Drained on size arrival; dropped on navigation to another graph.
    pendingSaveRef.current = null
    if (shouldStoreGroundTruth(pending, graphId, exportImageSize)) storeGroundTruthRef.current()
  }, [exportImageSize, graphId])

  // Ground Truth mode downloads the STORED ground truth — the answer key — never the
  // live preview, which a re-parse can move. No fallback to the preview: a file named
  // *.labelled.json must only ever contain what was stored.
  const gtDownloadInFlight = useRef(false)
  const downloadStoredGroundTruth = useCallback(async () => {
    if (!graphId || gtDownloadInFlight.current) return
    gtDownloadInFlight.current = true
    try {
      const { snapshot } = await apiService.getLatestGroundTruthSnapshot(graphId)
      if (!snapshot) {
        toast.info(
          // No nudge to Save: on a re-parsed sheet that would store the re-parsed graph
          // as the answer key. Completed sheets get their stored row from the backfill.
          t('graph_editor:jsonViewer.gtNotStored', { defaultValue: 'No stored ground truth for this drawing.' })
        )
        return
      }
      triggerDownload(
        new Blob([JSON.stringify(snapshot.payload, null, 2)], { type: 'application/json' }),
        groundTruthFilename(snapshot.payload)
      )
      toast.success(
        t('graph_editor:jsonViewer.gtDownloaded', {
          defaultValue: 'Downloaded the stored ground truth (saved {{when}}).',
          when: new Date(snapshot.created_at).toLocaleString(),
        })
      )
    } catch (err) {
      console.warn('Ground-truth download failed:', err)
      toast.error(
        t('graph_editor:jsonViewer.gtDownloadFailed', { defaultValue: 'Could not load the stored ground truth.' })
      )
    } finally {
      gtDownloadInFlight.current = false
    }
  }, [graphId, t])

  // Update setParsedGraph when graph changes
  useEffect(() => {
    if (!graph) return

    const graphWorker = new GraphWorker()
    graphWorker.postMessage({ graphData: graph, graphDocument })

    graphWorker.onmessage = (event) => {
      const { success, parsedGraph, error } = event.data

      if (success) {
        setParsedGraph(parsedGraph || null)
      } else {
        toast.error(
          t('data_catalog:graph_editor.toasts.load_error', {
            error: String(error),
          })
        )
      }
    }

    return () => {
      graphWorker.terminate()
    }
  }, [graph, graphDocument, t])

  // Initial fetch and parsing
  useEffect(() => {
    const abortController = new AbortController()
    let graphWorker: Worker | null = null
    let isMounted = true

    async function loadGraphData() {
      try {
        setLoading(true)
        // FS-621: clear any stale background image from a previously-loaded
        // graph BEFORE the async work. Without this, navigating from a loaded
        // Graph A to a still-processing Graph B leaves A's image set, so
        // `notReady` (= !loading && !backgroundImage) stays false after B's
        // load fails and the readiness poll never enables — B would hang.
        setBackgroundImage(null)
        // Phase 1: Fetch graph data and document metadata in parallel.
        // The doc fetch is best-effort: KG can render PnidDocument nodes
        // whose Postgres row was deleted out from under Neo4j (cleanup
        // sync gap). Treat a missing doc as null and render graph-only
        // rather than failing the whole preview.
        const [graphData, graphDoc] = await Promise.all([
          apiService.getGraphData(graphId, {
            signal: abortController.signal,
          }),
          apiService.getDocument(graphId, {
            signal: abortController.signal,
          }).catch((err) => {
            if ((err as Error)?.name === 'AbortError') throw err
            console.warn(
              `[EditorWrapper] document fetch failed for ${graphId}; rendering graph-only`,
              err,
            )
            return null
          }),
        ])

        if (!isMounted) return

        // Phase 2: Start graph parsing AND P&ID image download in parallel.
        // The pnidId is available from graphDoc right now — no need to wait
        // for the Web Worker to finish parsing before starting the download.
        const pnidIdValue = graphDoc?.properties?.pnid
        setPnidId(pnidIdValue)

        // Kick off P&ID resources download immediately (runs in parallel with worker)
        let pnidResourcesPromise: Promise<{ pnidDoc: DocumentRecord; imageBlob: Blob } | null> =
          Promise.resolve(null)
        if (pnidIdValue) {
          pnidResourcesPromise = Promise.all([
            apiService.getDocument(pnidIdValue, { signal: abortController.signal }),
            apiService.downloadPnidImage(pnidIdValue, { signal: abortController.signal, page: pnidPageNumber }),
          ])
            .then(([pnidDoc, imageBlob]) => ({ pnidDoc, imageBlob }))
            .catch((error) => {
              if ((error as Error).name !== 'AbortError') {
                console.error('Failed to load PNID resources:', error)
              }
              return null
            })
        }

        // Start graph parsing in Web Worker (runs in parallel with image download)
        graphWorker = new GraphWorker()
        graphWorker.postMessage({ graphData, graphDoc })

        graphWorker.onmessage = async (event) => {
          if (!isMounted) return

          const { success, parsedGraph, graphDoc: workerGraphDoc, error } = event.data

          if (success) {
            setParsedGraph(parsedGraph || null)
            if (parsedGraph?.nodes) {
              setProcessedGraphData(processGraphData(parsedGraph.nodes))
            }

            // Await P&ID resources — likely already resolved since the download
            // ran in parallel with graph parsing
            const pnidResources = await pnidResourcesPromise
            if (!isMounted) return

            if (pnidResources) {
              setPnidDocument(pnidResources.pnidDoc)

              // Use blob URL instead of base64 (instant vs CPU-intensive conversion)
              if (backgroundImageUrlRef.current) {
                URL.revokeObjectURL(backgroundImageUrlRef.current)
              }
              const imageUrl = URL.createObjectURL(pnidResources.imageBlob)
              backgroundImageUrlRef.current = imageUrl
              setBackgroundImage(imageUrl)
            }

            if (!isMounted) return

            // Set graphDocument after PNID document is fetched
            setGraphDocument(workerGraphDoc || null)

            // In read-only mode (boards), use local state to prevent cross-contamination
            // between multiple P&IDs. In edit mode, use global store.
            if (isReadOnly) {
              // Populate local state from parsedGraph
              const nodesMap = new Map<string, Node>()
              const edgesMap = new Map<string, Edge>()

              if (parsedGraph?.nodes) {
                parsedGraph.nodes.forEach((node: Node) => {
                  nodesMap.set(node.id, node)
                })
              }

              if (parsedGraph?.edges) {
                parsedGraph.edges.forEach((edge: Edge) => {
                  // Edges without an id can't be keyed/looked-up — skip them.
                  if (!edge.id) return
                  edgesMap.set(edge.id, {
                    id: edge.id,
                    fromNodeId: edge.fromNodeId,
                    toNodeId: edge.toNodeId,
                    relation: edge.relation,
                    properties: edge.properties,
                  })
                })
              }

              setLocalNodes(nodesMap)
              setLocalEdges(edgesMap)
            } else {
              // Use global store in edit mode — hydrate directly from
              // the worker's pre-parsed output to avoid re-parsing on
              // the main thread.
              hydrateState(
                parsedGraph?.nodes ?? [],
                parsedGraph?.edges ?? [],
                graphData,
                graphId,
              )
            }

            setLoading(false)
          } else {
            toast.error(
              t('data_catalog:graph_editor.toasts.load_error', {
                error: String(error),
              })
            )
            setLoading(false)
          }
        }
      } catch (error) {
        if (!isMounted || (error as Error).name === 'AbortError') return

        // FS-621: do NOT guess "still processing" from the fetch error. The old
        // code bucketed 404 / "not found" / "Internal Server Error" all into an
        // optimistic "still being processed" toast that never retried — hiding
        // real 5xx failures and spinning forever on a genuinely stuck doc.
        // Instead just stop loading; the readiness poll (enabled below whenever
        // the graph isn't loaded) hits GET /status — the source of truth — and
        // resolves to ready (reload), failed (surface error + retry), or
        // not_found (stop). See the loading/!backgroundImage render branch.
        setLoading(false)
      }
    }

    loadGraphData()

    return () => {
      isMounted = false
      abortController.abort()
      if (graphWorker) {
        graphWorker.terminate()
      }
    }
  }, [graphId, hydrateState, t, isReadOnly, pnidPageNumber, reloadKey])

  // FS-621: the editor shows the "not ready" view whenever it has finished its
  // load attempt without a background image. Poll GET /status while in that
  // view so the editor self-heals (ready → reload), surfaces a real failure, or
  // stops on a genuinely unknown id — replacing the old one-shot dead-end.
  const notReady = !loading && !backgroundImage
  const { state: graphState, errorMessage: graphErrorMessage } = useGraphStatusPoll(
    graphId,
    {
      enabled: notReady,
      onReady: useCallback(() => setReloadKey((k) => k + 1), []),
    }
  )

  // FS-621: human-gated reprocess from a failed/stalled graph. Retries the
  // parent document, then re-arms the poll for the new run. (The backend retry
  // gate is relaxed to accept the reconciler's 'stalled' state in a follow-up
  // PR; until then a stalled retry surfaces the error toast rather than hanging.)
  const handleRetryProcessing = useCallback(async () => {
    const targetId = documentId || graphId
    if (!targetId) return
    setRetrying(true)
    try {
      await apiService.retryDocumentProcessing(targetId)
      toast.success(
        t('data_catalog:graph_retry_started', 'Reprocessing started — this can take a few minutes.')
      )
      setReloadKey((k) => k + 1)
    } catch {
      toast.error(
        t('data_catalog:graph_retry_failed', 'Could not start reprocessing — please try again.')
      )
    } finally {
      setRetrying(false)
    }
  }, [documentId, graphId, t])

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (backgroundImageUrlRef.current) {
        URL.revokeObjectURL(backgroundImageUrlRef.current)
        backgroundImageUrlRef.current = null
      }
    }
  }, [])

  // Pending highlight — stored in a ref so it survives state resets during
  // loading. From the URL param (a cross-sheet navigation on the editor route,
  // which also opens the node's details), or from the `highlightNodeId` prop
  // (a preview landing on a tag: select and zoom to the node, but leave the
  // details panel closed — a read-only preview opening on an Approve button
  // is not what the reader came for, and the board's preview opens on none).
  type PendingHighlight = { id: string; fromUrl: boolean }
  const pendingHighlightRef = useRef<PendingHighlight | null>(
    highlightNodeParam
      ? { id: highlightNodeParam, fromUrl: true }
      : highlightNodeId
        ? { id: highlightNodeId, fromUrl: false }
        : null,
  )
  // Keep the ref in sync with URL param changes, and with the prop.
  useEffect(() => {
    if (highlightNodeParam) {
      pendingHighlightRef.current = { id: highlightNodeParam, fromUrl: true }
    } else if (highlightNodeId) {
      pendingHighlightRef.current = { id: highlightNodeId, fromUrl: false }
    }
  }, [highlightNodeParam, highlightNodeId])

  // Which drawing discipline this editor is annotating.
  //
  // The graph document's own `source_document_type` is authoritative. When it is
  // ABSENT — the fetch failed, or a projection dropped it — the editor still
  // renders graph-only, and defaulting straight to P&ID would silently show an
  // SLD the wrong taxonomy. So fall back to the PARENT document's type, which
  // carries the same answer, and only then to P&ID.
  //
  // Tests the FIELD, not the container: `properties` defaults to `{}` on write
  // and merges shallowly, so a stamp-less object is truthy and testing the
  // container would make this fallback unreachable.
  const discipline: DrawingDiscipline = hasDisciplineStamp(graphDocument?.properties)
    ? disciplineFromProperties(graphDocument?.properties)
    : pnidDocument?.type === 'sld'
      ? 'sld'
      : 'pnid'

  // Publish it to the route, which mounts its own provider for the panels this
  // component does not render.
  const onDisciplineResolvedRef = useRef(onDisciplineResolved)
  onDisciplineResolvedRef.current = onDisciplineResolved
  useEffect(() => {
    onDisciplineResolvedRef.current?.(discipline)
  }, [discipline])

  // ENG-2622's dashed outline marks a hand-drawn object so it reads differently
  // from a solid pipeline/OCR detection. That contrast only exists on a drawing
  // that HAS detections. An SLD has no parser, so every object is hand-drawn —
  // dashing all of them conveys nothing and just makes the whole drawing look
  // unlike a P&ID annotation. Suppress it there and render solid.
  const marksManualObjects = discipline === 'pnid'

  useEffect(() => {
    const newRectangles: Rectangle[] = []
    let hasAnyBoundingBox = false

    // Only check bounding boxes if we have nodes loaded
    if (activeNodes.size === 0) {
      // Don't update hasBoundingBoxes state when no data is loaded yet
      setRectangles([])
      return
    }

    for (const node of activeNodes.values()) {
      // Assign color based on node type
      let color: string = NODE_TYPE_COLORS.symbol // Default fallback

      if (node.type === 'symbol') {
        // Use purple for symbols that are instruments
        const symbolNode = node as SymbolNode
        color = symbolNode.is_instrument ? NODE_TYPE_COLORS.instrument_symbol : NODE_TYPE_COLORS.symbol
      } else if (node.type === 'tag') {
        color = NODE_TYPE_COLORS.tag
      } else if (node.type === 'connection') {
        color = NODE_TYPE_COLORS.connection
      } else if (node.type === 'table_cell') {
        color = NODE_TYPE_COLORS.table_cell
      }

      // Review layer: paint flagged low-confidence items in the one hot review
      // color and mute every other detection to a neutral grey at low opacity, so
      // the rare cases stand alone. "Full color" restores the per-class palette
      // (muted is the default). Detected/Original layers keep the base color.
      let reviewOpacity: number | undefined
      if (isReviewLayer) {
        const rs = reviewStyleFor(node, reviewThreshold, reviewFullColor, reviewSymbolsOnly)
        if (rs.color) color = rs.color
        reviewOpacity = rs.opacity
      }

      if (
        node.type === 'symbol' ||
        node.type === 'tag'
      ) {
        // Skip nodes without bounding box data
        if (
          node.x === undefined ||
          node.y === undefined ||
          node.width === undefined ||
          node.height === undefined
        ) {
          console.warn(
            `Node ${node.id} is missing bounding box data - will only be visible in graph preview mode`
          )
          continue
        }

        hasAnyBoundingBox = true

        // Check if this node should be highlighted
        const isHighlighted = node.id === highlightedNodeId || node.id === crossSheetHighlightId
        const isSearchMatch = searchMatchNodeIds.has(node.id) && !isHighlighted

        // A junction renders as a dot whether it was placed manually
        // (name="junction") or detected by the pipeline (category="junction"),
        // so the two are visually identical.
        const isJunctionNode =
          (node as SymbolNode).name === 'junction' ||
          (node as SymbolNode).category === 'junction'
        newRectangles.push({
          id: node.id,
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
          color: isHighlighted ? NODE_TYPE_COLORS.highlighted : isSearchMatch ? NODE_TYPE_COLORS.searchMatch : color,
          // Don't mute a node the user is actively focused on (queue jump / search)
          // — the highlight/search color takes over, so the box must stay fully visible.
          opacity: isHighlighted || isSearchMatch ? undefined : reviewOpacity,
          // A junction is a tiny dot sitting at a line tee. Lines carry a 0-area
          // placeholder bbox and win the same-zIndex "smaller on top" sort, so they
          // paint over the dot (it disappears under the stroke). Lift junctions above
          // lines/boxes so the dot stays visible — still under highlight/search.
          zIndex: isHighlighted ? 10 : isSearchMatch ? 5 : isJunctionNode ? 2 : 0,
          vertices: node.vertices,
          isJunction: isJunctionNode,
          // ENG-2622: hand-drawn objects render with a dashed outline so they
          // don't look like OCR/pipeline detections (solid).
          isManual: marksManualObjects && node.source === 'manual',
        })
      } else if (node.type === 'table_cell') {
        // Drawn only while the Tables grid shows this table. Deliberately does NOT set
        // `hasAnyBoundingBox` — that gates the editor tabs, and a transient overlay
        // flipping it as the panel opens would make the tab bar flicker.
        if (visibleTableIndex === null || node.table_index !== visibleTableIndex) continue
        if (
          node.x === undefined || node.y === undefined ||
          node.width === undefined || node.height === undefined
        ) continue
        const isHighlighted = node.id === highlightedNodeId || node.id === focusedTableCellId
        newRectangles.push({
          id: node.id,
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
          color: isHighlighted ? NODE_TYPE_COLORS.highlighted : color,
          // Above the drawing's own boxes, so a cell isn't hidden under the diagram.
          zIndex: isHighlighted ? 10 : 3,
          isManual: marksManualObjects && node.source === 'manual',
        })
      } else if (node.type === 'connection') {
        // Handle connection nodes with LINESTRING geometry
        const connectionNode = node as ConnectionNode
        if (connectionNode.geometry_wkt && connectionNode.geometry_wkt.includes('LINESTRING')) {
          // A drawn line IS a visible object, so it keeps the Editor tab alive.
          // Counting only symbols/tags here was fine while every drawing arrived
          // from the parser with detections, but an SLD starts EMPTY: drawing a
          // line as the first object left `hasAnyBoundingBox` false, the sub-navbar
          // dropped the Editor tab out from under the annotator (it is gated on
          // `hasBoundingBoxes`), and the view fell through to Graph Preview — the
          // drawing appeared to vanish mid-annotation, replaced by a single node card.
          hasAnyBoundingBox = true
          const isHighlighted = node.id === highlightedNodeId || node.id === crossSheetHighlightId

          newRectangles.push({
            id: node.id,
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            color: isHighlighted ? NODE_TYPE_COLORS.highlighted : color,
            opacity: isHighlighted ? undefined : reviewOpacity,
            zIndex: isHighlighted ? 10 : 0,
            geometry_wkt: connectionNode.geometry_wkt,
            isManual: marksManualObjects && node.source === 'manual',
          })
        }
      }
    }

    setRectangles(newRectangles)
    // Only update if we found at least one node, otherwise keep the current state
    // This prevents false negatives when graph has no visible nodes (symbols/tags)
    const hasVisibleNodes =
      newRectangles.length > 0 ||
      Array.from(activeNodes.values()).some(
        (n) =>
          n.type === 'symbol' || n.type === 'tag'
      )
    if (hasVisibleNodes || !hasAnyBoundingBox) {
      setHasBoundingBoxes(hasAnyBoundingBox)
    }

    // Apply the pending highlight after rectangles are built.
    const pending = pendingHighlightRef.current
    if (pending && newRectangles.some(r => r.id === pending.id)) {
      const nodeId = pending.id
      pendingHighlightRef.current = null
      setCrossSheetHighlightId(nodeId)
      // Clear regular highlight so it doesn't block crossSheetHighlightId in selectedNodeId prop
      setHighlightedNodeId(null)
      if (pending.fromUrl) {
        // Open the details panel for the highlighted node
        const targetNode = activeNodes.get(nodeId)
        if (targetNode) {
          setActiveTooltip({
            nodeId,
            x: targetNode.x ?? 0,
            y: targetNode.y ?? 0,
          })
        }
        // Clean up the URL param so it doesn't re-trigger on browser back/forward
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev)
          next.delete('highlightNode')
          return next
        }, { replace: true })
      }
    }
  }, [activeNodes, highlightedNodeId, searchMatchNodeIds, crossSheetHighlightId, setSearchParams, isReviewLayer, reviewThreshold, reviewFullColor, reviewSymbolsOnly, visibleTableIndex, focusedTableCellId, marksManualObjects])

  // Ordered list of flagged (low-confidence) items for the current sheet, in
  // reading order (top→bottom, left→right). Drives the Review queue: counter,
  // clickable list, and next/prev stepping. Recomputed when the sheet or the
  // threshold changes. The filter/sort/label pipeline lives in buildReviewQueue
  // (unit-tested in reviewLayer.test.ts).
  const flaggedReviewItems = useMemo(
    () => buildReviewQueue(Array.from(activeNodes.values()), reviewThreshold, reviewSymbolsOnly),
    [activeNodes, reviewThreshold, reviewSymbolsOnly],
  )

  // The queue cursor is derived from the actual canvas focus (highlightedNodeId),
  // not a separate state, so the highlighted list row and the panned-to node can
  // never drift apart when the threshold or sheet changes (−1 = focus is not on a
  // flagged item).
  const currentReviewIndex = useMemo(
    () => flaggedReviewItems.findIndex((it) => it.id === highlightedNodeId),
    [flaggedReviewItems, highlightedNodeId],
  )

  // Jump the canvas to the flagged item at `index` (wraps around), reusing the
  // single-node focus → zoomToRectangle path via highlightedNodeId.
  const jumpToReviewItem = useCallback(
    (index: number) => {
      if (flaggedReviewItems.length === 0) return
      const len = flaggedReviewItems.length
      const clamped = ((index % len) + len) % len
      setHighlightedNodeId(flaggedReviewItems[clamped].id)
    },
    [flaggedReviewItems],
  )

  // Notify parent component when bounding box status changes
  useEffect(() => {
    onBoundingBoxStatusChange?.(hasBoundingBoxes)
  }, [hasBoundingBoxes, onBoundingBoxStatusChange])

  // Update processedGraphData when active nodes change
  useEffect(() => {
    const nodes = Array.from(activeNodes.values())
    setProcessedGraphData(processGraphData(nodes))
  }, [activeNodes])

  // Incremental commit: create-or-update the in-progress connection node
  // from the full point list, returning its id. Created on the first call with
  // >=2 points (adding the start edge if the line is anchored to a start node);
  // updated in place on every later call so the line grows as a real, persisted
  // connection rather than a preview held until the loop closes.
  const upsertConnectionLine = (allPoints: Point2D[]): string | null => {
    if (isReadOnly || allPoints.length < 2) return connectionLineId
    const geometry = `LINESTRING (${allPoints.map((p) => `${p.x} ${p.y}`).join(', ')})`
    if (connectionLineId) {
      // Read straight from the node map (not getActiveNodeById, which throws on a
      // missing id): if the in-progress node was deleted out from under us, skip.
      const node = activeNodes.get(connectionLineId) as ConnectionNode | undefined
      if (node) updateActiveNode({ ...node, geometry_wkt: geometry })
      return connectionLineId
    }
    // Snapshot the pre-line state on first commit (mirrors handleAdd / handlePolygonAdd)
    // so a single Undo removes the whole drawn line and its endpoint edges. The line
    // commits incrementally, but this only fires once — when the node is created.
    recordCanvasSnapshot()
    const id = uuidv4()
    // Carry the Add-Object panel pre-fill onto the drawn line — classification,
    // Tag (`label`), and Note. A line is created here (not via handleAdd), so
    // without this it lost everything the user pre-filled except the seeded
    // Piping/Line default. Set once at creation; the geometry-only update branch
    // above spreads `...node`, so these survive subsequent points.
    // New objects are created PENDING (no confidence) — they require an explicit Approve
    // (gated on required classification fields) via the Add Object / Node Details panel.
    addNodes([{
      id,
      type: 'connection',
      geometry_wkt: geometry,
      pnid_level_i: addNodeConfig.pnid_level_i,
      pnid_level_ii: addNodeConfig.pnid_level_ii,
      pnid_level_iii: addNodeConfig.pnid_level_iii,
      label: addNodeConfig.label,
      note: addNodeConfig.note,
      annotation_text: addNodeConfig.annotation_text,
      source: 'manual' as const,
    } as ConnectionNode])
    // Orient connectivity edges consistently as line → target (the end anchor at
    // finishConnectionOnSymbol and the junction wiring already do this). A uniform
    // via-line → target direction lets the GT exporter attribute each endpoint to its
    // connecting line unambiguously — required now that an endpoint can itself be another
    // line (line↔line / object↔line), where a target→line start anchor would be
    // indistinguishable from the other line's own end anchor (ENG-2613).
    // Safe to flip: every other edge consumer reads edges direction-agnostically — deletion and
    // neighbour-lookup use `from===id || to===id`, persistence stores both, and the canvas renders
    // only tag↔connection edges (checking both orderings). No consumer keys off the start-anchor
    // direction; the KG-preview arrow is the only direction-aware reader and it's cosmetic.
    if (connectionStartNodeId) addActiveEdge({ id: uuidv4(), fromNodeId: id, toNodeId: connectionStartNodeId })
    setConnectionLineId(id)
    return id
  }

  // Stop drawing, keeping whatever line was already committed.
  const resetConnectionDraw = () => {
    setConnectionStartNodeId(null)
    setConnectionStartPointAbs(null)
    setConnectionVertices([])
    setConnectionHoveredId(null)
    setHighlightedNodeId(null)
    setConnectionLineId(null)
  }

  // Discard the in-progress line entirely (Esc): since it was committed as you
  // drew, cancelling must delete the node so nothing is left behind.
  const discardConnectionDraw = () => {
    if (connectionLineId) deleteActiveNodeById(connectionLineId)
    resetConnectionDraw()
  }

  // Drop a routing vertex (direction change) and commit it: the segment becomes
  // part of the growing connection immediately. Free angle by default; Shift
  // constrains the new segment to horizontal/vertical.
  const addRoutingVertex = (clicked: Point2D, shiftKey: boolean) => {
    if (!connectionStartPointAbs) return
    const prevPt = connectionVertices.length > 0 ? connectionVertices[connectionVertices.length - 1] : connectionStartPointAbs
    const snapped = shiftKey ? orthogonalSnap(prevPt, clicked) : clicked
    const newVertices = [...connectionVertices, snapped]
    setConnectionVertices(newVertices)
    upsertConnectionLine([connectionStartPointAbs, ...newVertices])
    setLineRedoStack([]) // a new line action invalidates pending segment redo
    // A fresh line segment is now the most-recent action, so Undo should peel it
    // (not restore an earlier snapshot).
    lastActionWasSnapshotRef.current = false
  }

  // Remove the last placed routing vertex from the in-progress line (Backspace /
  // Undo while drawing). Re-commits the shortened line, or drops the committed
  // node entirely when only the start anchor is left.
  const popConnectionVertex = () => {
    if (connectionVertices.length === 0) return
    const newVertices = connectionVertices.slice(0, -1)
    setConnectionVertices(newVertices)
    if (connectionStartPointAbs && newVertices.length >= 1) {
      upsertConnectionLine([connectionStartPointAbs, ...newVertices])
    } else if (connectionLineId) {
      deleteActiveNodeById(connectionLineId)
      setConnectionLineId(null)
    }
  }

  // Parse / serialize a connection's LINESTRING geometry.
  const parseLineWkt = (wkt: string): Point2D[] =>
    wkt.replace(/^\s*LINESTRING\s*\(/i, '').replace(/\)\s*$/, '').split(',').map((p) => {
      const [px, py] = p.trim().split(/\s+/).map(Number)
      return { x: px, y: py }
    })
  const toLineWkt = (pts: Point2D[]): string =>
    `LINESTRING (${pts.map((p) => `${p.x} ${p.y}`).join(', ')})`

  // ENG-2583: snap a connection endpoint onto a target node's actual geometry —
  // a polygon's traced outline or a line's path — not just a rectangular bbox, so
  // a drawn line links cleanly to polygons and other lines too (boxes still use
  // snapToBoundary). Falls back to the raw point for a node with no usable shape.
  const snapToNode = (point: Point2D, node: Node | undefined): Point2D => {
    if (!node) return point
    const verts = (node as { vertices?: Point2D[] }).vertices
    if (verts && verts.length >= 3) return nearestPointOnPath(point, verts, true).point
    const wkt = (node as ConnectionNode).geometry_wkt
    if (wkt) {
      // Snap to the nearest point across every (MULTI)LINESTRING sub-segment, like
      // the hit-test — a multi-segment pipe must snap to its path, not its bbox.
      // parseLineStringSegments yields [x,y] tuples (NaN-filtered, ≥2 pts/segment).
      let best = point
      let bestDist = Infinity
      for (const seg of parseLineStringSegments(wkt)) {
        const r = nearestPointOnPath(point, seg.map((p) => ({ x: p[0], y: p[1] })), false)
        if (r.dist < bestDist) {
          bestDist = r.dist
          best = r.point
        }
      }
      if (bestDist < Infinity) return best
    }
    return snapToBoundary(point, node)
  }

  // Peel the last point off the selected finished line — one Undo = one segment —
  // keeping it selected so its panel stays open. Reads the LIVE store so repeated
  // clicks each remove exactly one point (no stale-closure idempotency).
  const peelSelectedLineVertex = (): boolean => {
    const selId = activeTooltip?.nodeId
    if (!selId) return false
    const node = useAppStore.getState().currentState.nodes.get(selId)
    if (!node || node.type !== 'connection') return false
    const conn = node as ConnectionNode
    // Only peel single LINESTRING polylines (what the draw tool produces); leave
    // any pre-existing MULTILINESTRING connections alone.
    if (!conn.geometry_wkt || !/^\s*LINESTRING\s*\(/i.test(conn.geometry_wkt)) return false
    const pts = parseLineWkt(conn.geometry_wkt)
    if (pts.length <= 2) {
      // Last segment peeled → remove the line and any edges into it (e.g. the
      // start-symbol connection). Stash the removed edges too so redo restores them.
      const removedEdges: Edge[] = []
      useAppStore.getState().currentState.edges.forEach((edge, edgeId) => {
        if (edge.fromNodeId === conn.id || edge.toNodeId === conn.id) {
          removedEdges.push(edge)
          deleteActiveEdgeById(edgeId)
        }
      })
      setLineRedoStack((prev) => [...prev, { node: { ...conn }, edges: removedEdges }])
      deleteActiveNodeById(conn.id)
      setActiveTooltip(null)
    } else {
      setLineRedoStack((prev) => [...prev, { node: { ...conn }, edges: [] }])
      updateActiveNode({ ...conn, geometry_wkt: toLineWkt(pts.slice(0, -1)) })
    }
    return true
  }

  // Undo (canvas-only — never classification): mid-line, pop the in-progress
  // vertex; else peel the selected line's last segment; else step the last drawn
  // symbol/polygon back. Redo re-adds.
  const handleUndo = () => {
    if (isReadOnly) return
    if (connectionVertices.length > 0) { popConnectionVertex(); return }
    // If the last action was a delete/add/move snapshot, restore it before peeling
    // a line that merely happens to still be selected (e.g. select a line, then
    // delete other nodes → Undo should bring the deleted nodes back, not peel).
    if (lastActionWasSnapshotRef.current && useAppStore.getState().undoPast.length > 0) {
      undo()
      return
    }
    if (peelSelectedLineVertex()) return
    undo()
  }
  const handleRedo = () => {
    if (isReadOnly) return
    const restore = lineRedoStack[lineRedoStack.length - 1]
    if (restore) {
      setLineRedoStack((prev) => prev.slice(0, -1))
      if (useAppStore.getState().currentState.nodes.has(restore.node.id)) {
        updateActiveNode(restore.node)
      } else {
        addNodes([restore.node])
        if (restore.edges.length) useAppStore.getState().addEdges(restore.edges)
      }
      const first = restore.node.geometry_wkt ? parseLineWkt(restore.node.geometry_wkt)[0] : { x: 0, y: 0 }
      selectNode(restore.node.id, first.x, first.y)
      return
    }
    redo()
  }
  // Keep the latest handlers in refs so the keydown listener stays stable (no
  // per-render re-subscribe) yet never goes stale.
  const handleUndoRef = useRef(handleUndo)
  handleUndoRef.current = handleUndo
  const handleRedoRef = useRef(handleRedo)
  handleRedoRef.current = handleRedo
  const selNodeForUndo = activeTooltip?.nodeId ? activeNodes.get(activeTooltip.nodeId) : undefined
  // Only a LINESTRING connection can be peeled; don't enable Undo for other
  // connections (it would otherwise fall through and undo an unrelated action).
  const canPeelSelectedLine = selNodeForUndo?.type === 'connection'
    && /^\s*LINESTRING\s*\(/i.test((selNodeForUndo as ConnectionNode).geometry_wkt ?? '')
  const canUndoNow = undoPast.length > 0 || connectionVertices.length > 0 || canPeelSelectedLine
  const canRedoNow = undoFuture.length > 0 || lineRedoStack.length > 0

  // Finish the in-progress line on a double-click. The double-click
  // lands two near-coincident clicks, so drop trailing duplicate points, re-commit
  // the cleaned line, then stop drawing — the committed line stays; the next click
  // starts a new one.
  // `selectAfter` opens the just-drawn line's details panel (pick the line type /
  // remove) — only the explicit double-click finish wants that. Callers that end a
  // draw as a side effect (tool switch, panel close/reconfigure) leave it false so
  // they don't set activeTooltip and unexpectedly swap the Add panel for the
  // details panel.
  const finishConnectionDraw = (selectAfter = false) => {
    let drawnLineId: string | null = null
    let anchor: Point2D | null = null
    if (connectionStartPointAbs && connectionVertices.length > 0) {
      const verts = trimTrailingDuplicateVertices(connectionStartPointAbs, connectionVertices)
      if (verts.length === 0) {
        // Every point collapsed onto the start — e.g. a double-click with no real
        // segment drawn. The line committed as it was drawn is degenerate, so
        // discard the node rather than leaving a zero-length connection behind.
        discardConnectionDraw()
        return
      }
      if (verts.length !== connectionVertices.length) {
        upsertConnectionLine([connectionStartPointAbs, ...verts])
      }
      // Capture before resetConnectionDraw() clears these.
      drawnLineId = connectionLineId
      anchor = connectionStartPointAbs
    }
    resetConnectionDraw()
    if (drawnLineId) attachSymbolsToLine(drawnLineId)
    // Parity with drawn symbols: open the new line's details panel without a manual
    // re-click — but only on an explicit finish (see selectAfter above).
    if (selectAfter && drawnLineId && anchor) selectNode(drawnLineId, anchor.x, anchor.y)
  }

  // A click that lands on a symbol ENDS the line there: drop the final point
  // (snapped to that symbol's boundary, mirroring the start anchor), commit, and
  // link the end to the symbol — so connectivity is captured from a confirmed click
  // rather than inferred by proximity. No double-click needed to connect two
  // objects: click the first, click the second.
  const finishConnectionOnSymbol = (clicked: Point2D, shiftKey: boolean, nodeId: string) => {
    if (!connectionStartPointAbs) return
    // Re-clicking the start symbol before any segment is drawn is a no-op (not a
    // zero-length self-connection) — keep drawing from the start.
    if (nodeId === connectionStartNodeId && connectionVertices.length === 0) return
    const prevPt = connectionVertices.length > 0 ? connectionVertices[connectionVertices.length - 1] : connectionStartPointAbs
    const snapped = shiftKey ? orthogonalSnap(prevPt, clicked) : clicked
    const endNode = activeNodes.get(nodeId)
    const endPoint = snapToNode(snapped, endNode)
    // No trailing-dedup here (unlike the double-click finish): this is a single,
    // deliberate click on a symbol, so the end point is kept even when it lands close
    // to the start — adjacent symbols must still connect.
    const lineId = upsertConnectionLine([connectionStartPointAbs, ...connectionVertices, endPoint])
    // Link end → symbol (the start edge is created in upsertConnectionLine). Skip a
    // self-link when the line starts and ends on the same symbol.
    if (lineId && nodeId !== connectionStartNodeId) {
      addActiveEdge({ id: uuidv4(), fromNodeId: lineId, toNodeId: nodeId })
    }
    if (lineId) attachSymbolsToLine(lineId)
    const anchor = connectionStartPointAbs
    resetConnectionDraw()
    // Select the finished line so its vertex handles show right away — parity with
    // the Esc/Enter finish and with drawn shapes.
    if (lineId) selectNode(lineId, anchor.x, anchor.y)
  }


  const handleClick = (rectangle: ClickEvent) => {
    // Clear cross-sheet highlight on any user click
    if (crossSheetHighlightId) setCrossSheetHighlightId(null)

    // While the Tables grid is open, clicking a cell belongs to the grid, not the Node
    // Details panel. Handled before the generic selection so it doesn't also open that
    // panel over the grid.
    if (rectangle.nodeId && onTableCellClick && visibleTableIndex !== null) {
      // `getActiveNodeById` throws for an unknown id in the read-only path, and this runs on
      // every canvas click — a stale rectangle would take the whole handler down.
      const clicked = activeNodes.get(rectangle.nodeId)
      if (clicked?.type === 'table_cell') {
        // Clicking the SAME cell again picks it off, matching the renderer's selection toggle.
        // Without this the cell kept its highlight colour (it is drawn highlighted while it is
        // `highlightedNodeId` or the grid's focused cell) and still looked selected after a
        // second click, even though the multi-selection had already dropped it.
        const alreadyPicked = highlightedNodeId === rectangle.nodeId
        onTableCellClick(alreadyPicked ? null : rectangle.nodeId)
        setHighlightedNodeId(alreadyPicked ? null : rectangle.nodeId)
        // The cell IS the selection now, so drop any node the details panel had open —
        // otherwise its highlight lingers behind the grid.
        setActiveTooltip(null)
        return
      }
    }

    // Connection creation flow when in Create mode with Connection type
    // Single-click model: the FIRST click starts the line (anchored to a
    // symbol if clicked on one), and EVERY click after adds a point — on a box,
    // edge, or empty space alike. No click is hijacked into "end on" / "tee onto" /
    // "reset", so tracing along equipment outlines registers every point. The line
    // commits as you draw; to draw a SEPARATE line, start a new connection
    // (Add Object → Line). Esc scraps it, Backspace undoes a point.
    if (mode === ModeType.Create && addNodeConfig.shape === 'line') {
      if (!rectangle.absolute) return
      if (!connectionStartPointAbs) {
        // First click: start. Anchor to a symbol's boundary if clicked on one
        // (keeps the start-side link); otherwise a free start point.
        if (rectangle.nodeId) {
          const startNode = getActiveNodeById(rectangle.nodeId)
          setConnectionStartNodeId(rectangle.nodeId)
          setConnectionStartPointAbs(snapToNode(rectangle.absolute, startNode))
          setActiveTooltip(null)
          setHighlightedNodeId(rectangle.nodeId)
        } else {
          setConnectionStartPointAbs(rectangle.absolute)
        }
        return
      }
      // A subsequent click that lands on a target object (symbol, polygon, or
      // another line) ENDS the line there (single-click finish + link). A click in
      // open space — or back onto the in-progress line itself — adds a routing point
      // and keeps drawing (free angle; Shift = 90°); double-click / Enter ends a
      // line in open space.
      if (rectangle.nodeId && rectangle.nodeId !== connectionLineId) {
        finishConnectionOnSymbol({ x: rectangle.absolute.x, y: rectangle.absolute.y }, !!rectangle.shiftKey, rectangle.nodeId)
      } else {
        addRoutingVertex({ x: rectangle.absolute.x, y: rectangle.absolute.y }, !!rectangle.shiftKey)
      }
      return
    }

    if (rectangle.nodeId) {
      // Clicking the currently-open node toggles the panel closed.
      // Clicking a different node updates the panel in place (stays open).
      if (activeTooltip?.nodeId === rectangle.nodeId) {
        setActiveTooltip(null)
        setHighlightedNodeId(null)
        setIsFromReviewFlow(false)
        // Also close the review drawer — otherwise toggling the flyout
        // closed from a review-flow click leaves the drawer orphaned.
        setIsDrawerOpen(false)
        return
      }

      const clickedNode = getActiveNodeById(rectangle.nodeId)
      trackEvent('graph-node_selected', { entity_type: clickedNode?.type || 'unknown' })

      const tooltipData: TooltipData = {
        nodeId: rectangle.nodeId,
        x: rectangle.screen.x,
        y: rectangle.screen.y,
      }
      setActiveTooltip(tooltipData)
      setHighlightedNodeId(rectangle.nodeId) // Highlight the clicked node
      setIsFromReviewFlow(false) // Reset when clicking directly on canvas
      // Close the ControlPanel when a node is clicked for details
      if (mode === ModeType.Create || panningFromCreate) {
        setPanningFromCreate(false)
        setMode(ModeType.Drag)
      }
    } else {
      // Only suppress clearing when the ControlPanel is actively kept open via panning
      if (!(mode === ModeType.Drag && panningFromCreate)) {
        setActiveTooltip(null)
        setHighlightedNodeId(null) // Clear highlight when clicking canvas
      }
    }
  }

  // Snapshot the canvas for Undo and mark it the most-recent undoable action, so
  // Undo restores this snapshot rather than peeling a still-selected line. Also
  // clears the line-segment redo stack (a new canvas action invalidates it).
  const recordCanvasSnapshot = () => {
    useAppStore.getState().recordHistory()
    setLineRedoStack([])
    lastActionWasSnapshotRef.current = true
  }

  const handleDelete = (deleteEvent: DeleteEvent) => {
    if (deleteEvent.nodeIds.length > 0) {
      // Snapshot before deleting so a single Undo restores the nodes (and edges).
      recordCanvasSnapshot()
    }
    for (const nodeId of deleteEvent.nodeIds) {
      deleteActiveNodeById(nodeId)
    }
    trackEvent('graph-node_deleted', { node_count: deleteEvent.nodeIds.length, is_bulk: deleteEvent.nodeIds.length > 1 })
    // Clear selection and connection detection markers after delete
    setSelectedNodeIds(new Set())
    setConnectionMarkers(null)
  }

  const handleBulkDelete = () => {
    if (selectedNodeIds.size === 0) return
    // Snapshot before the bulk delete so a single Undo restores every node + edge
    // (e.g. select-all → delete-all → undo brings the whole drawing back).
    recordCanvasSnapshot()
    const count = selectedNodeIds.size
    deleteActiveMultipleNodes(Array.from(selectedNodeIds))
    trackEvent('graph-node_deleted', { node_count: count, is_bulk: true })
    setSelectedNodeIds(new Set())
    setConnectionMarkers(null)
    toast.success(
      t('data_catalog:graph_editor.toasts.bulk_delete_success', {
        count,
      })
    )
  }

  // ENG-2624: apply a classification to every selected object at once. Reuses the
  // same pnid_level_i/ii/iii fields the single-node panel writes; one Undo step.
  const handleBulkClassify = (levels: BulkClassification) => {
    if (isReadOnly || selectedNodeIds.size === 0) return
    recordCanvasSnapshot()
    const count = selectedNodeIds.size
    updateMultipleNodes(Array.from(selectedNodeIds), {
      pnid_level_i: levels.pnid_level_i,
      pnid_level_ii: levels.pnid_level_ii,
      pnid_level_iii: levels.pnid_level_iii,
    })
    trackEvent('graph-nodes_reclassified', { node_count: count })
    toast.success(
      t('data_catalog:graph_editor.bulk_actions.reclassify_success', {
        count,
        defaultValue: 'Re-classified {{count}} objects',
      })
    )
  }

  // ENG-2632: fill (value) or clear ('') a text field across every selected object.
  // Reuses updateMultipleNodes (one Undo step); planBulkFieldUpdate routes the "Tag"
  // field to `label` (symbol/connection/table_cell) vs `content` (tag nodes).
  const applyBulkField = (field: BatchTextField, value: string) => {
    if (isReadOnly || selectedNodeIds.size === 0) return
    recordCanvasSnapshot()
    const ids = Array.from(selectedNodeIds)
    for (const group of planBulkFieldUpdate(currentState.nodes, ids, field, value)) {
      updateMultipleNodes(group.ids, group.patch)
    }
    const cleared = value === ''
    const fieldLabel = String(t(FIELD_LABEL_KEY[field]))
    trackEvent(cleared ? 'graph-nodes_field_cleared' : 'graph-nodes_field_filled', {
      node_count: ids.length,
      field,
    })
    toast.success(
      cleared
        ? t('data_catalog:graph_editor.bulk_actions.clear_success', {
            count: ids.length,
            field: fieldLabel,
            defaultValue: 'Cleared {{field}} on {{count}} objects',
          })
        : t('data_catalog:graph_editor.bulk_actions.fill_success', {
            count: ids.length,
            field: fieldLabel,
            defaultValue: 'Set {{field}} on {{count}} objects',
          })
    )
  }
  const handleBulkFillField = applyBulkField
  const handleBulkClearField = (field: BatchTextField) => applyBulkField(field, '')

  // ENG-2624: Delete/Backspace removes the single object open in the Node Details
  // panel. Multi-selection (Select mode) is handled by PnidRenderer's own keydown
  // over `selectedRectangles`; this fills the gap for the default single-select
  // flow, where pressing Delete/Backspace previously did nothing. The decision
  // (incl. not firing while a multi-selection exists, so the two handlers can't
  // both delete on one keypress) lives in `shouldDeleteActiveSelection`.
  useEffect(() => {
    const selectedId = activeTooltip?.nodeId
    // V2-only: the V2 editor uses activeTooltip as its single-selection source.
    if (!selectedId || !isV2 || activeTab !== EDITOR_MODE) return
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (
        !shouldDeleteActiveSelection({
          key: e.key,
          targetTag: target.tagName,
          isContentEditable: target.isContentEditable,
          hasActiveSelection: true,
          multiSelectionCount: selectedNodeIds.size,
          // Deletion is permitted in a read-only board preview when
          // allowEntityDeletion is set, matching the panel's Remove button.
          isReadOnly: isReadOnly && !allowEntityDeletion,
        })
      ) {
        return
      }
      e.preventDefault() // block browser back-navigation on Backspace
      recordCanvasSnapshot() // one Undo restores the node + its edges
      // Route through the same delete the Remove button uses: in a read-only
      // (allow-deletion) preview, deleteActiveNodeById handles the local-state
      // path; otherwise deleteActiveMultipleNodes also cleans up connected edges.
      if (isReadOnly) {
        deleteActiveNodeById(selectedId)
      } else {
        deleteActiveMultipleNodes([selectedId])
      }
      // Intentionally no success toast — keyboard delete should feel instant and
      // is reversible via Undo (the panel's Remove button toasts; this stays quiet).
      trackEvent('graph-node_deleted', { node_count: 1, is_bulk: false })
      setActiveTooltip(null)
      setConnectionMarkers(null)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
    // The delete helpers + recordCanvasSnapshot are recreated each render, so
    // they're intentionally omitted (adding them would re-attach the listener on
    // every render). Every reactive value they read IS a dependency: isReadOnly,
    // allowEntityDeletion, and the local-state maps deleteActiveNodeById reads in
    // read-only mode (localNodes/localEdges) — so the captured closures stay fresh.
  }, [activeTooltip, selectedNodeIds, isReadOnly, allowEntityDeletion, isV2, activeTab, localNodes, localEdges]) // eslint-disable-line react-hooks/exhaustive-deps

  // ENG-2624: the single-node details panel and a real multi-selection (2+) are
  // mutually exclusive. Once a multi-selection forms, close the single panel and
  // drop its individual highlight — the node is now folded into the selection, so
  // a lingering orange outline shouldn't survive after the selection is cleared.
  // A size-1 selection keeps the single panel.
  useEffect(() => {
    if (selectedNodeIds.size >= 2) {
      setActiveTooltip(null)
      setHighlightedNodeId(null)
    }
  }, [selectedNodeIds])

  // ENG-2624: entering Create mode (e.g. "+ Add Object", which switches mode
  // directly and bypasses handleToolChange) must drop any multi-selection —
  // otherwise it survives the Create round-trip and a later bulk Delete/Apply
  // would hit nodes the user believed were deselected.
  useEffect(() => {
    if (mode === ModeType.Create) setSelectedNodeIds(new Set())
  }, [mode])

  const handleRectangleUpdate = (updatedRectangles: Rectangle[]) => {
    // Update each rectangle in the node store
    // onRectangleUpdate fires once on pointer-up (the per-frame drag goes to local
    // temp state), so snapshotting on the first real change here gives one Undo per
    // move/resize gesture — not per frame.
    let snapshotted = false
    for (const rect of updatedRectangles) {
      const node = getActiveNodeById(rect.id)
      if (!node) continue

      // ENG-2582: a polygon edit (move/add/delete a vertex) changes the ring,
      // so compare vertices too and carry them through — otherwise the traced
      // shape (and its POLYGON round-trip on save) would silently revert.
      const verticesChanged =
        JSON.stringify(node.vertices ?? null) !== JSON.stringify(rect.vertices ?? null)
      // A connection-line vertex drag changes only its LINESTRING geometry
      // (x/y/w/h stay put), so detect + persist that too.
      const geometryChanged =
        rect.geometry_wkt != null && (node as ConnectionNode).geometry_wkt !== rect.geometry_wkt

      // Only update if values actually changed to avoid unnecessary updates
      if (
        node.x !== rect.x ||
        node.y !== rect.y ||
        node.width !== rect.width ||
        node.height !== rect.height ||
        verticesChanged ||
        geometryChanged
      ) {
        if (!snapshotted) {
          // Snapshot before the first change so a single Undo reverts the gesture.
          recordCanvasSnapshot()
          snapshotted = true
        }
        const isConnection = !!rect.geometry_wkt
        updateActiveNode({
          ...node,
          // A connection rect carries a placeholder 0,0,0,0 bbox (it renders from
          // geometry_wkt, not a box), so a vertex drag persists only the LINESTRING
          // and leaves the node's real bbox intact instead of zeroing it.
          ...(isConnection ? {} : { x: rect.x, y: rect.y, width: rect.width, height: rect.height }),
          ...(rect.vertices ? { vertices: rect.vertices } : {}),
          ...(rect.geometry_wkt ? { geometry_wkt: rect.geometry_wkt } : {}),
        })
        // A polygon reshape fits its outline the way a box resize does (see handleRectangleResizeComplete).
        if (takesLines(node) && reshaped(node.vertices, rect.vertices)) attachLinesToSymbol(rect.id)

        // Moving/resizing a SYMBOL drags any connected line's endpoint with it, so
        // the line stays attached instead of overflowing past the box. Only simple
        // drawn LINESTRINGs are followed — a parsed MULTILINESTRING pipe is left
        // alone (parseLineWkt would NaN-corrupt it) — and the attached end is chosen
        // by proximity to the symbol, not edge direction (parsed edge directions vary).
        if (!isConnection && rect.width && rect.height) {
          const newBbox = { x: rect.x, y: rect.y, width: rect.width, height: rect.height }
          const newVerts = rect.vertices && rect.vertices.length >= 3 ? rect.vertices : null
          const oldCx = (node.x ?? rect.x) + (node.width ?? 0) / 2
          const oldCy = (node.y ?? rect.y) + (node.height ?? 0) / 2
          // A pure box MOVE (same size, shifted) carries its connected endpoints with
          // it; a box RESIZE or a polygon vertex RESHAPE moves only part of the shape,
          // so the endpoint is re-snapped in place instead of translated.
          const isBoxMove = !newVerts && node.width === rect.width && node.height === rect.height
          // A junction (dot) keeps its linked endpoint at its CENTER — where placement
          // put it. Re-snapping to the bbox edge would nudge it ~half the dot off and
          // read as the line detaching from a 10px point.
          // Reuse the value already derived in the rectangles build (rect.isJunction)
          // rather than re-deriving from the raw node fields.
          const isJunctionNode = !!rect.isJunction
          const newCenter = { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 }
          const reSnap = (endpoint: Point2D): Point2D => {
            // Polygon: re-snap onto the reshaped outline at the point nearest where the
            // endpoint already sits — no translate, so an attachment on an edge that
            // didn't move stays put instead of sliding to a corner.
            if (newVerts) return nearestPointOnPath(endpoint, newVerts, true).point
            if (isJunctionNode) return newCenter
            // Box: translate with the box only on a MOVE (so a box dragged toward the
            // line keeps the endpoint on the facing edge and never overflows past it);
            // on a RESIZE snap in place, so an edge that didn't move keeps the endpoint.
            const ref = isBoxMove
              ? { x: endpoint.x + (rect.x - (node.x ?? rect.x)), y: endpoint.y + (rect.y - (node.y ?? rect.y)) }
              : endpoint
            return nearestBoundaryPoint(ref, newBbox)
          }
          // Move only the endpoints of lines actually LINKED to this node (edge-based).
          // We deliberately do NOT proximity-grab unlinked lines near a junction on a
          // move — that would re-route an unrelated pipe that merely passes nearby.
          // Lines get linked at placement (the junction proximity-attach and the drawn
          // symbol's line attach in handleAdd) or by an explicit connection draw, so a
          // real attachment already has an edge.
          activeEdges.forEach((edge) => {
            const lineId = edge.fromNodeId === rect.id ? edge.toNodeId : edge.toNodeId === rect.id ? edge.fromNodeId : null
            if (!lineId) return
            const lineNode = activeNodes.get(lineId) as ConnectionNode | undefined
            const wkt = lineNode?.geometry_wkt
            if (!wkt || !/^\s*LINESTRING\s*\(/i.test(wkt)) return
            const pts = parseLineWkt(wkt)
            if (pts.length < 2 || pts.some((p) => !Number.isFinite(p.x) || !Number.isFinite(p.y))) return
            const dFirst = Math.hypot(pts[0].x - oldCx, pts[0].y - oldCy)
            const dLast = Math.hypot(pts[pts.length - 1].x - oldCx, pts[pts.length - 1].y - oldCy)
            if (dFirst <= dLast) pts[0] = reSnap(pts[0])
            else pts[pts.length - 1] = reSnap(pts[pts.length - 1])
            updateActiveNode({ ...lineNode, geometry_wkt: toLineWkt(pts) })
          })
        }
      }
    }
  }

  // Select a node and open its details panel (category / subcategory / type +
  // delete). Used right after a draw (auto-select) and after a resize so
  // the user never has to re-click a freshly created object to classify or delete
  // it. Coordinates are passed in rather than looked up, because right after
  // addNode the rendered `activeNodes` snapshot can still be stale.
  const selectNode = (nodeId: string, x: number, y: number) => {
    setActiveTooltip({ nodeId, x, y })
    setHighlightedNodeId(nodeId)
    setIsFromReviewFlow(false)
  }

  const handleRectangleResizeComplete = (
    rectangleId: string,
    handle?: string,
    before?: TableRegion,
    after?: TableRegion,
  ) => {
    // Plain Map lookup for the type test below: `getActiveNodeById` throws when the id is
    // unknown in read-only mode, and this only needs to know whether it is a table cell.
    const resized = activeNodes.get(rectangleId)

    // While the Tables grid is open, resizing a cell belongs to the grid — same rule
    // `handleClick` already applies to clicking one.
    //
    // Without this, resizing a cell CLOSED the Tables panel: the `selectNode` below sets
    // `activeTooltip`, the effect above fires `onNodeSelected`, and the editor route answers
    // that with `setActiveRightPanel(null)` — which unmounts the grid the cell belongs to.
    // Reported externally as being forced out of the feature by anything but a tiny change,
    // and the "tiny" part fits: under MIN_DRAG_DISTANCE_IN_PIXELS the renderer never commits
    // a resize, so this callback never fired at all.
    if (resized?.type === 'table_cell' && visibleTableIndex !== null) {
      // Move the whole column/row boundary, Excel-style — widening a column shifts the
      // ones after it. Every cell owns its geometry, so a bare resize would leave a gap or
      // an overlap against its neighbours.
      if (!handle || !before || !after) {
        // Never silently skip: a missing rect means the whole feature no-ops, which is
        // indistinguishable from "the maths is wrong" from the outside. It cost several
        // rounds of chasing the wrong layer once already.
        console.warn('[tables] wall coupling skipped — no handle/geometry', { handle, before, after })
      } else {
        // `activeNodes` here is a render-time closure, so it still holds the PRE-drag
        // geometry even though `handleRectangleUpdate` committed the dragged cell one
        // statement earlier. That is exactly what the coupling wants for the neighbours;
        // the dragged cell's own before/after comes in as arguments.
        const model = groupCellsIntoTables(
          Array.from(activeNodes.values()).filter(
            (n): n is TableCellNode => n.type === 'table_cell',
          ),
        ).find((tb) => tb.tableIndex === visibleTableIndex)
        if (model) {
          // No snapshot here on purpose. `handleRectangleUpdate` already took one on
          // pointer-up, BEFORE the dragged cell changed, and it runs first — so a single
          // Undo reverts the drag and the wall together. Snapshotting again would split
          // one gesture into two Undo steps.
          for (const patch of coupleWallResize(model, rectangleId, handle, before, after)) {
            const cell = activeNodes.get(patch.id)
            if (!cell) continue
            const { id: _id, ...geometry } = patch
            updateActiveNode({ ...cell, ...geometry })
          }
        }
      }
      onTableCellClick?.(rectangleId)
      setHighlightedNodeId(rectangleId)
      setActiveTooltip(null)
      return
    }

    // Open the details panel for the resized rectangle. Read through the store rather than
    // the `activeNodes` closure above: the closure predates the resize commit, so the panel
    // would anchor at the shape's pre-drag position.
    const node = getActiveNodeById(rectangleId)
    // Fitting a box to its object can bring line ends into reach.
    if (node && takesLines(node)) attachLinesToSymbol(rectangleId)
    if (node && node.x !== undefined && node.y !== undefined) {
      selectNode(rectangleId, node.x, node.y)
    }
  }

  // ENG-2582: create a freeform polygon object from traced vertices. Unlike the
  // rectangle path there's no bbox contour-refinement or auto connection
  // detection — the shape is the user's exact outline (bbox is derived for
  // selection). Persists as a POLYGON ring on save.
  const handlePolygonAdd = (vertices: Array<{ x: number; y: number }>) => {
    if (vertices.length < 3) return
    const { x, y, width, height } = bboxOfVertices(vertices)

    const newNodeId = uuidv4()
    const baseNode = {
      id: newNodeId,
      x,
      y,
      width,
      height,
      vertices,
      pnid_level_i: addNodeConfig.pnid_level_i,
      pnid_level_ii: addNodeConfig.pnid_level_ii,
      pnid_level_iii: addNodeConfig.pnid_level_iii,
      note: addNodeConfig.note,
      annotation_text: addNodeConfig.annotation_text,
      source: 'manual' as const,
    }
    const newNode: Node =
      addNodeConfig.type === 'tag'
        ? { ...baseNode, type: 'tag', content: addNodeConfig.label || '' }
        : ({ ...baseNode, type: 'symbol', name: 'general', label: addNodeConfig.label } as Node)

    recordCanvasSnapshot()
    useAppStore.getState().addNode(newNode)
    trackEvent('graph-node_created', { document_id: graphId, entity_type: addNodeConfig.type, source: 'manual-polygon' })
    if (takesLines(newNode)) attachLinesToSymbol(newNodeId)
    toast.success(t('graph_editor:editor.objectAdded', { defaultValue: 'Object added' }))
    // Select the just-drawn object so its category/options panel opens
    // immediately and it can be deleted without a manual re-click.
    selectNode(newNodeId, x, y)
  }

  const handleAdd = async (addEvent: AddEvent, skipBoundaryDetection = false) => {
    // In table-region mode the drawn box isn't an object — it's the frame the Tables panel
    // fills. Report it and create nothing.
    if (drawTableRegion) {
      if (addEvent.width > 0 && addEvent.height > 0) {
        onTableRegionDrawn?.({
          x: addEvent.x, y: addEvent.y, width: addEvent.width, height: addEvent.height,
        })
      }
      return
    }
    let finalEvent = addEvent
    // ENG-2613: a point/junction is a fixed-size dot — never boundary-refine it.
    const isPoint = addNodeConfig.shape === 'point'

    // Auto-refine bbox: when detectConnections is on, try OpenCV contour detection
    // at the bbox center. If it finds a contour SMALLER than the drawn bbox, use it
    // (more precise fit). If bigger or not found, keep the user's drawn bbox.
    if (!isPoint && !skipBoundaryDetection && addNodeConfig.detectConnections && graphId) {
      try {
        const centerX = addEvent.x + addEvent.width / 2
        const centerY = addEvent.y + addEvent.height / 2
        const boundaryResult = await apiService.detectEquipmentBoundary(graphId, centerX, centerY)
        if (boundaryResult.detected && boundaryResult.boundary) {
          const b = boundaryResult.boundary
          const drawnArea = addEvent.width * addEvent.height
          const detectedArea = b.width * b.height
          // Use detected boundary if it's smaller (tighter fit) than what user drew
          if (detectedArea < drawnArea) {
            finalEvent = { x: b.x, y: b.y, width: b.width, height: b.height }
            setDetectedBoundary({ x: b.x, y: b.y, width: b.width, height: b.height })
            toast.info(t('graph_editor:editor.refinedToBoundary', { width: b.width.toFixed(0), height: b.height.toFixed(0), defaultValue: 'Refined to equipment boundary ({{width}}x{{height}})' }))
          } else {
            setDetectedBoundary(null)
          }
        }
      } catch {
        // Contour detection failed — use drawn bbox as-is
      }
    }

    const newNodeId = uuidv4()
    const baseNode = {
      id: newNodeId,
      x: finalEvent.x,
      y: finalEvent.y,
      width: finalEvent.width,
      height: finalEvent.height,
      pnid_level_i: addNodeConfig.pnid_level_i,
      pnid_level_ii: addNodeConfig.pnid_level_ii,
      pnid_level_iii: addNodeConfig.pnid_level_iii,
      note: addNodeConfig.note,
      annotation_text: addNodeConfig.annotation_text,
      source: 'manual' as const,
    }

    let newNode: Node
    if (addNodeConfig.type === 'tag') {
      newNode = {
        ...baseNode,
        type: 'tag',
        content: addNodeConfig.label || '',
      }
    } else {
      // Stamp the raw-vision `category` the generators classify on. The generator
      // buckets junctions off `category === "junction"`; every OTHER symbol is
      // buckets off `category` too — and a manual add with NO category emits as a
      // bare "unknown"/structural node (no tag/label, absent from the instrument/
      // equipment rollups) and lands `category=null` in db_ask's raw/. Derive it
      // from the picked pnid class so the add is classified in BOTH KG tiers.
      //
      // P&ID only. `resolveVisionCategory` maps the P&ID sheet's Level I values,
      // so an SLD's "Electrical" would fall through to its Equipment default and
      // label a breaker as process equipment in a tier that does not read SLDs
      // anyway. An SLD add carries its classification in pnid_level_* alone.
      const stampsVisionCategory = discipline === 'pnid'
      const symbolCategory = isPoint
        ? 'junction'
        : stampsVisionCategory && addNodeConfig.pnid_level_i
          ? resolveVisionCategory(addNodeConfig.pnid_level_i as PnidLevelI, addNodeConfig.pnid_level_ii)
          : null
      // Fine subtype (e.g. Gate Valve → valve_gate) → symbols[].type → db_ask dexpi_class,
      // so subtype queries (gate vs ball) find the add — not just the coarse category.
      const symbolType =
        isPoint || !stampsVisionCategory ? null : resolveVisionType(addNodeConfig.pnid_level_iii)
      newNode = {
        ...baseNode,
        type: 'symbol',
        name: isPoint ? 'junction' : 'general',
        ...(symbolCategory ? { category: symbolCategory } : {}),
        ...(symbolType ? { symbol_type: symbolType } : {}),
        label: addNodeConfig.label,
      } as Node
    }

    recordCanvasSnapshot()
    useAppStore.getState().addNode(newNode)
    // A point's panel-derived type is Connection (Instrumentation/Line resolves that
    // way), but it's placed as a junction symbol — log it as such, not 'connection'.
    trackEvent('graph-node_created', { document_id: graphId, entity_type: isPoint ? 'junction' : addNodeConfig.type, source: 'manual' })
    // Select the just-drawn object so its category/options panel opens immediately
    // and it can be deleted without a manual re-click. Stays in Create mode so the
    // Add panel returns when the details panel is closed (draw → classify → draw).
    selectNode(newNodeId, finalEvent.x, finalEvent.y)
    // A point wires itself to the lines it sits on below; a box links the lines ending at it.
    if (!isPoint && takesLines(newNode)) attachLinesToSymbol(newNodeId)

    // A junction (point) wires itself to the lines it sits on, immediately and
    // client-side: snap any LINESTRING endpoint within reach onto the junction and
    // create the junction↔line edge. This is what makes dropping a T on a tee
    // actually connect — the backend bbox-match + confirm flow below only links
    // endpoints to *symbols* and never produces a junction↔line edge. Endpoint-based:
    // a line that PASSES THROUGH (no endpoint at the dot) is not split (follow-up).
    if (isPoint && graphId) {
      const cx = finalEvent.x + finalEvent.width / 2
      const cy = finalEvent.y + finalEvent.height / 2
      const catchRadius = Math.max(finalEvent.width, finalEvent.height) / 2 + JUNCTION_LINK_CATCH_PX
      let linked = 0
      activeNodes.forEach((n) => {
        if (n.type !== 'connection') return
        const wkt = (n as ConnectionNode).geometry_wkt
        if (!wkt || !/^\s*LINESTRING\s*\(/i.test(wkt)) return
        const pts = parseLineWkt(wkt)
        if (pts.length < 2 || pts.some((p) => !Number.isFinite(p.x) || !Number.isFinite(p.y))) return
        // Snap exactly one endpoint (never both → no zero-length collapse); see
        // junctionAttachEndpoint.
        const idx = junctionAttachEndpoint(pts, { x: cx, y: cy }, catchRadius)
        if (idx === null) return
        pts[idx] = { x: cx, y: cy }
        updateActiveNode({ ...n, geometry_wkt: toLineWkt(pts) })
        addActiveEdge({ id: uuidv4(), fromNodeId: n.id, toNodeId: newNodeId })
        linked++
      })
      if (linked > 0) {
        toast.success(t('graph_editor:editor.junctionLinked', { count: linked, defaultValue: 'Junction linked to {{count}} line(s)' }))
      }
    }

    // Detect connections if enabled and node is a symbol (the bbox-spatial backend
    // match + confirm flow). A point/junction is explicitly excluded — it uses the
    // direct proximity-attach above, not this symbol-to-symbol confirm flow.
    if (!isPoint && addNodeConfig.detectConnections && addNodeConfig.type !== 'tag' && graphId) {
      try {
        const result = await apiService.detectManualSymbolConnections(graphId, {
          x: finalEvent.x,
          y: finalEvent.y,
          width: finalEvent.width,
          height: finalEvent.height,
        })

        if (result.connected_symbols.length > 0 || result.dangling_lines.length > 0) {
          const edgesToAdd = result.connected_symbols.map((conn) => ({
            id: uuidv4(),
            from_node: conn.symbol_id,
            to_node: newNodeId,
          }))

          // Build node properties — persist the new symbol to the knowledge graph
          const nodeProperties = [
            { id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'source', value: 'manual' },
            { id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'type', value: newNode.type },
            // Use finalEvent (post-contour-refinement) so saved geometry matches
            // the node that was rendered + used everywhere else in this handler.
            { id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'x', value: String(finalEvent.x) },
            { id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'y', value: String(finalEvent.y) },
            { id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'width', value: String(finalEvent.width) },
            { id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'height', value: String(finalEvent.height) },
            ...(addNodeConfig.label ? [{ id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'label', value: addNodeConfig.label }] : []),
            ...(addNodeConfig.pnid_level_i ? [{ id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'pnid_level_i', value: addNodeConfig.pnid_level_i }] : []),
            ...(addNodeConfig.pnid_level_ii ? [{ id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'pnid_level_ii', value: addNodeConfig.pnid_level_ii }] : []),
            ...(addNodeConfig.pnid_level_iii ? [{ id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'pnid_level_iii', value: addNodeConfig.pnid_level_iii }] : []),
            ...(addNodeConfig.note ? [{ id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'note', value: addNodeConfig.note }] : []),
          ]

          // Build edge properties for symbol connections
          const edgeProperties = result.connected_symbols.flatMap((conn) => {
            const edgeEntry = edgesToAdd.find((e) => e.from_node === conn.symbol_id)
            if (!edgeEntry) return []
            return [
              { id: uuidv4(), target_id: edgeEntry.id, is_node_property: false, key: 'relation', value: 'connected_to' },
              { id: uuidv4(), target_id: edgeEntry.id, is_node_property: false, key: 'connection_method', value: 'manual_bbox_spatial_match' },
              { id: uuidv4(), target_id: edgeEntry.id, is_node_property: false, key: 'original_line_id', value: conn.line_id },
              { id: uuidv4(), target_id: edgeEntry.id, is_node_property: false, key: 'snap_point', value: JSON.stringify(conn.snap_point) },
              { id: uuidv4(), target_id: edgeEntry.id, is_node_property: false, key: 'original_endpoint', value: JSON.stringify(conn.original_endpoint) },
            ]
          })

          // Build tag association edges (has_label) — same pattern as pipeline
          const tagEdges = (result.associated_tags ?? [])
            .filter((tag) => tag.id)
            .map((tag) => ({
              id: uuidv4(),
              from_node: newNodeId,
              to_node: tag.id,
            }))

          const tagEdgeProperties = tagEdges.map((edge) => ({
            id: uuidv4(),
            target_id: edge.id,
            is_node_property: false,
            key: 'relation',
            value: 'has_label',
          }))

          // Capture context so drag-to-correct can rebuild the patch later
          manualSymbolContextRef.current = {
            newNodeId,
            bbox: { x: finalEvent.x, y: finalEvent.y, width: finalEvent.width, height: finalEvent.height },
            config: addNodeConfig,
            // The tag case is handled earlier (addNodeConfig.type === 'tag' guard
            // above), so type is narrowed to Symbol | Connection here — always 'symbol'.
            nodeType: 'symbol',
            tagIds: (result.associated_tags ?? []).filter((t) => t.id).map((t) => t.id),
          }

          // Hold the patch — don't save until user confirms
          setPendingGraphPatch({
            patch: {
              nodes_to_add: [newNodeId],
              nodes_to_delete: [],
              edges_to_add: [...edgesToAdd, ...tagEdges],
              edges_to_delete: [],
              properties_to_upsert: [...nodeProperties, ...edgeProperties, ...tagEdgeProperties],
              properties_to_delete: [],
            },
            connectedCount: result.connected_symbols.length,
            danglingCount: result.dangling_lines.length,
          })

          toast.info(
            `Found ${result.connected_symbols.length} connection(s)` +
            (result.dangling_lines.length > 0 ? ` · ${result.dangling_lines.length} dangling` : '') +
            ' — click "Save to Graph" to persist'
          )

          setConnectionMarkers({
            connectedPoints: result.connected_symbols.map((c) => ({
              x: c.snap_point[0],
              y: c.snap_point[1],
              markerId: uuidv4(),
              symbolId: c.symbol_id,
              lineId: c.line_id,
            })),
            danglingPoints: result.dangling_lines.map((d) => ({
              x: d.endpoint_near_bbox[0],
              y: d.endpoint_near_bbox[1],
              markerId: uuidv4(),
              lineId: d.line_id,
            })),
            candidatePoints: (result.candidate_points ?? []).map((p) => ({
              x: p.x,
              y: p.y,
              lineId: p.line_id,
              symbolId: p.symbol_id,
            })),
          })
        } else {
          toast.info(t('graph_editor:editor.noConnectionsDetected', { defaultValue: 'No connections detected near this symbol' }))
          setConnectionMarkers(null)
        }
      } catch (error) {
        console.error('Failed to detect connections:', error)
        toast.error(t('graph_editor:editor.connectionDetectionFailed', { defaultValue: 'Connection detection failed — symbol added without connections' }))
        setConnectionMarkers(null)
      }
    }
  }

  const handleSaveToGraph = async () => {
    if (!pendingGraphPatch || !graphId) return
    try {
      await apiService.updateGraph(graphId, pendingGraphPatch.patch)
      toast.success(t('graph_editor:editor.savedConnections', { count: pendingGraphPatch.connectedCount, defaultValue: 'Saved {{count}} connection(s) to graph' }))
      setPendingGraphPatch(null)
      setConnectionMarkers(null)
      setDetectedBoundary(null)
    } catch (error) {
      console.error('Failed to save to graph:', error)
      toast.error(t('graph_editor:editor.saveConnectionsFailed', { defaultValue: 'Failed to save connections to graph' }))
    }
  }

  const handleDiscardDetection = () => {
    // Remove the node that was optimistically added before detection
    const ctx = manualSymbolContextRef.current
    if (ctx) {
      deleteActiveNodeById(ctx.newNodeId)
    }
    setPendingGraphPatch(null)
    setConnectionMarkers(null)
    setDetectedBoundary(null)
    manualSymbolContextRef.current = null
  }

  // Rebuild the pending patch from the current markers + captured context.
  // Called after user drags a marker to a new candidate point.
  const rebuildPendingPatchFromMarkers = useCallback(
    (markers: NonNullable<typeof connectionMarkers>) => {
      const ctx = manualSymbolContextRef.current
      if (!ctx) return
      const { newNodeId, bbox, config, nodeType, tagIds } = ctx

      const edgesToAdd = markers.connectedPoints.map((m) => ({
        id: uuidv4(),
        from_node: m.symbolId,
        to_node: newNodeId,
      }))

      const nodeProperties = [
        { id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'source', value: 'manual' },
        { id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'type', value: nodeType },
        { id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'x', value: String(bbox.x) },
        { id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'y', value: String(bbox.y) },
        { id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'width', value: String(bbox.width) },
        { id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'height', value: String(bbox.height) },
        ...(config.label ? [{ id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'label', value: config.label }] : []),
        ...(config.pnid_level_i ? [{ id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'pnid_level_i', value: config.pnid_level_i }] : []),
        ...(config.pnid_level_ii ? [{ id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'pnid_level_ii', value: config.pnid_level_ii }] : []),
        ...(config.pnid_level_iii ? [{ id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'pnid_level_iii', value: config.pnid_level_iii }] : []),
        ...(config.note ? [{ id: uuidv4(), target_id: newNodeId, is_node_property: true, key: 'note', value: config.note }] : []),
      ]

      const edgeProperties = markers.connectedPoints.flatMap((m, i) => {
        const edge = edgesToAdd[i]
        return [
          { id: uuidv4(), target_id: edge.id, is_node_property: false, key: 'relation', value: 'connected_to' },
          { id: uuidv4(), target_id: edge.id, is_node_property: false, key: 'connection_method', value: 'manual_bbox_spatial_match' },
          { id: uuidv4(), target_id: edge.id, is_node_property: false, key: 'original_line_id', value: m.lineId },
          { id: uuidv4(), target_id: edge.id, is_node_property: false, key: 'snap_point', value: JSON.stringify([m.x, m.y]) },
        ]
      })

      const tagEdges = tagIds.map((tagId) => ({
        id: uuidv4(),
        from_node: newNodeId,
        to_node: tagId,
      }))
      const tagEdgeProperties = tagEdges.map((edge) => ({
        id: uuidv4(),
        target_id: edge.id,
        is_node_property: false,
        key: 'relation',
        value: 'has_label',
      }))

      setPendingGraphPatch({
        patch: {
          nodes_to_add: [newNodeId],
          nodes_to_delete: [],
          edges_to_add: [...edgesToAdd, ...tagEdges],
          edges_to_delete: [],
          properties_to_upsert: [...nodeProperties, ...edgeProperties, ...tagEdgeProperties],
          properties_to_delete: [],
        },
        connectedCount: markers.connectedPoints.length,
        danglingCount: markers.danglingPoints.length,
      })
    },
    [],
  )

  // Called when user drags a marker and drops it on a candidate point.
  // If the candidate has a symbol, marker becomes a green connection.
  // If not, marker becomes a red dangling line.
  const handleMarkerDrop = useCallback(
    (markerId: string, candidate: { x: number; y: number; lineId: string; symbolId: string | null }) => {
      setConnectionMarkers((prev) => {
        if (!prev) return prev

        // Remove the old marker (from either list)
        const connectedPoints = prev.connectedPoints.filter((m) => m.markerId !== markerId)
        const danglingPoints = prev.danglingPoints.filter((m) => m.markerId !== markerId)

        // Add the new marker based on candidate's symbol
        if (candidate.symbolId) {
          connectedPoints.push({
            x: candidate.x,
            y: candidate.y,
            markerId,
            symbolId: candidate.symbolId,
            lineId: candidate.lineId,
          })
        } else {
          danglingPoints.push({
            x: candidate.x,
            y: candidate.y,
            markerId,
            lineId: candidate.lineId,
          })
        }

        const next = { ...prev, connectedPoints, danglingPoints }
        rebuildPendingPatchFromMarkers(next)
        return next
      })
    },
    [rebuildPendingPatchFromMarkers],
  )

  const handleToolChange = (type: string) => {
    trackEvent('graph-tool_changed', { tool_name: type })

    // Hand tool in Create mode — keep Add Node panel open, skip state clearing
    if (type === 'hand' && mode === ModeType.Create) {
      setPanningFromCreate(true)
      setMode(ModeType.Drag)
      return
    }

    // Leaving temporary pan — clear the flag
    setPanningFromCreate(false)

    // Switching to another tool ends any in-progress connection draw: keep a
    // valid line committed, discard a degenerate one, and clear the hover halo.
    finishConnectionDraw()

    // Preserve the selection when switching between the Select and Move (Hand)
    // tools — both support the multi-selection + bulk panel, so a marquee (or
    // Cmd/Ctrl+click) selection shouldn't vanish just because you grab Move to
    // pan or delete. Only clear when leaving to a non-selection tool. (ENG-2624)
    const keepsSelection = toolChangePreservesSelection(type)
    if (!keepsSelection) {
      setActiveTooltip(null)
      setHighlightedNodeId(null)
      setSelectedNodeIds(new Set())
    }

    // Close search when switching to any other tool
    if (isEditorSearchOpen) {
      setIsEditorSearchOpen(false)
      setSearchMatchNodeIds(new Set())
    }

    // Close comment panel when switching to a non-comment tool
    if (type !== 'comment') {
      setIsCommentPanelOpen(false)
    }

    switch (type) {
      case 'select':
        return setMode(ModeType.Select)
      case 'hand':
        return setMode(ModeType.Drag)
      case 'comment':
        return setIsCommentPanelOpen(prev => !prev)
      default:
        return setMode(ModeType.Select)
    }
  }

  // The moment the user puts down the first segment of a line (the
  // connection node is first committed), pop a hint on how to finish it. Show it
  // only once per editor session — the connection id changes for every new line,
  // so without this guard the hint would re-fire on each line the user starts.
  const connectionHintShownRef = useRef(false)
  useEffect(() => {
    if (connectionLineId && !connectionHintShownRef.current) {
      connectionHintShownRef.current = true
      toast.info(
        t('graph_editor:editor.connectionDrawHint', {
          defaultValue: 'Click a symbol to connect to it · double-click, Esc, or Enter to finish · Backspace undoes a point.',
        }),
      )
    }
  }, [connectionLineId])

  // Esc / Enter complete the in-progress connection; Backspace pops the last vertex.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (mode !== ModeType.Create || addNodeConfig.shape !== 'line') return
      // Skip when typing in a form field so we don't hijack input editing —
      // including SELECT, so Enter confirms a dropdown (Category/Type) instead of
      // finishing the line.
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) return

      if (e.key === 'Escape' || e.key === 'Enter') {
        // Esc / Enter COMPLETE the in-progress line (commit it and keep it selected
        // for vertex editing) — a friendlier finish than double-click, which could
        // leave a tiny stray segment. A degenerate line is discarded inside
        // finishConnectionDraw; Backspace peels points if you need to back out.
        if (connectionLineId || connectionStartPointAbs || connectionVertices.length > 0) {
          e.preventDefault()
          finishConnectionDraw(true)
        }
      } else if (e.key === 'Backspace' && connectionVertices.length > 0) {
        e.preventDefault() // stop the browser Back shortcut
        popConnectionVertex()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [mode, addNodeConfig.shape, connectionStartPointAbs, connectionVertices, connectionLineId])

  // Word-style undo/redo: Cmd/Ctrl+Z undoes; Cmd/Ctrl+Shift+Z or Cmd/Ctrl+Y
  // redoes. Ignored while typing in a field so native text undo still works.
  useEffect(() => {
    if (isReadOnly || activeTab !== EDITOR_MODE || !hasBoundingBoxes) return
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      const key = e.key.toLowerCase()
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault()
        handleUndoRef.current()
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault()
        handleRedoRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isReadOnly, activeTab, hasBoundingBoxes])

  // Keyboard shortcut: Cmd+F / Ctrl+F to open search. Where the V2 sub-navbar
  // owns search, it handles the shortcut itself.
  useEffect(() => {
    if (!ownsFloatingSearch) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        if (activeTab === EDITOR_MODE && hasBoundingBoxes) {
          e.preventDefault()
          setIsEditorSearchOpen(true)
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeTab, hasBoundingBoxes, ownsFloatingSearch])

  // Listen for download event from V2 sub-navbar. The handler invokes the live
  // ref (FS-622) so it always renders the current rectangles + pnidDocument;
  // re-subscribing on activeTab keeps the Editor-Mode gate correct.
  useEffect(() => {
    const handleDownload = () => {
      if (activeTab === EDITOR_MODE) downloadPnidImageRef.current()
    }
    window.addEventListener('graph-download', handleDownload)
    return () => window.removeEventListener('graph-download', handleDownload)
  }, [activeTab])


  const handleSearchMatchChange = useCallback(
    (currentNodeId: string | null, allMatchIds: Set<string>) => {
      setHighlightedNodeId(currentNodeId)
      setSearchMatchNodeIds(allMatchIds)
    },
    []
  )

  // V2 external inline search — driven by the sub-navbar search input
  const { matchingNodeIds: extMatchIds, currentMatchIndex: extMatchIdx, goToNext: extGoToNext, goToPrevious: extGoToPrevious } =
    useNodeSearch(activeNodes, externalSearchTerm ?? '')

  useEffect(() => {
    if (!isV2 || externalSearchTerm === undefined) return
    if (extMatchIds.length === 0) {
      handleSearchMatchChange(null, new Set())
    } else {
      const safeIndex = Math.min(extMatchIdx, extMatchIds.length - 1)
      handleSearchMatchChange(extMatchIds[safeIndex], new Set(extMatchIds))
    }
    // JSON and Graph Preview tabs drive their own match info — don't overwrite.
    if (activeTab === JSON_VIEW || activeTab === GRAPH_PREVIEW) return
    onSearchMatchInfo?.({ total: extMatchIds.length, current: extMatchIdx })
  }, [isV2, externalSearchTerm, activeTab, extMatchIds, extMatchIdx, handleSearchMatchChange, onSearchMatchInfo])

  // Expose canvas-search navigation callbacks to the parent via refs.
  // On JSON / Graph Preview tabs, the respective renderer owns the refs.
  useEffect(() => {
    if (activeTab === JSON_VIEW || activeTab === GRAPH_PREVIEW) return
    if (onSearchNext) onSearchNext.current = extGoToNext
    if (onSearchPrevious) onSearchPrevious.current = extGoToPrevious
  }, [activeTab, extGoToNext, extGoToPrevious, onSearchNext, onSearchPrevious])

  const nodeDetailsPanelNode = activeTooltip ? (() => { try { return getActiveNodeById(activeTooltip.nodeId) } catch { return undefined } })() : undefined
  // ENG-2588: the Node Details / Add Node panels are editor-canvas UI and render
  // as flex siblings OUTSIDE the per-tab blocks — gate them on Editor Mode so
  // they don't linger over the Graph Preview / KG Preview / JSON tabs.
  // ENG-2624: a real multi-selection (2+) shows the Node Details panel in its bulk
  // state; a single selection (0 or 1) shows the full single-node panel, so
  // per-node fields stay reachable even in Select mode.
  const showMultiSelectPanel = isV2 && activeTab === EDITOR_MODE && !isReadOnly && !externalPanelOpen
    && selectedNodeIds.size >= 2 && (mode === ModeType.Select || mode === ModeType.Drag)
  const showNodeDetailsPanel = isV2 && activeTab === EDITOR_MODE && !!activeTooltip && !externalPanelOpen && selectedNodeIds.size <= 1
  const showAddNodePanel = isV2 && activeTab === EDITOR_MODE && mode === ModeType.Create && !activeTooltip && !externalPanelOpen

  return (
    // The active taxonomy for every panel below — read from the graph's own
    // `source_document_type`, so a P&ID and an SLD open the same editor with
    // different Category / Subcategory / Type options.
    <TaxonomyProvider discipline={discipline}>
    <div className="w-full h-full flex overflow-hidden">
    <div className="flex-1 min-w-0 relative overflow-hidden bg-[#f5f5f5] dark:bg-dark-muted">
      {!isV2 && graphDocument && !isReadOnly && hasBoundingBoxes && (
        <header className="w-full absolute top-0 flex items-center justify-between bg-white dark:bg-dark-card border-b border-gray-200 dark:border-dark-border p-4 py-4 z-10">
          <div className="flex gap-4 items-center justify-between">
            <FileBox />
            <h1 className="font-semibold">
              {pnidDocument
                ? cleanDocumentName(pnidDocument.name, pnidDocument.type)
                : cleanDocumentName(graphDocument.name, graphDocument.type)}
            </h1>
            <ChevronRight />
            <p className="font-semibold">
              {/* Was hardcoded to 1, so a multi-sheet drawing read "Sheet 1" on
                  every page while the page bar beside it said otherwise. */}
              {t('data_catalog:graph_editor.header.sheet_label', { number: currentSheetIndex + 1 })}
            </p>
            {sheetNavigation}
          </div>

          {activeTab === EDITOR_MODE && (
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => setIsEditorSearchOpen(true)}
                className="flex items-center gap-2"
                title={t('graph_editor:editor.searchNodesTitle', { defaultValue: 'Search nodes (Ctrl+F / Cmd+F)' })}
              >
                <Search className="w-4 h-4" />
                {t('graph_editor:editor.searchButton', { defaultValue: 'Search' })}
              </Button>
              <Button
                variant="outline"
                onClick={downloadPnidImage}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                {t('graph_editor:jsonViewer.download', { defaultValue: 'Download' })}
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  setIsDrawerOpen(true)
                  setIsFromReviewFlow(true)
                }}
              >
                {t('data_catalog:graph_editor.header.review_pending')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setHighlightedNodeId(null)
                  setSelectedNodeIds(new Set())
                  setMode(ModeType.Create)
                }}
              >
                {t('data_catalog:graph_editor.header.add_object')}
              </Button>
            </div>
          )}
        </header>
      )}
      {loading || !backgroundImage ? (
        <div
          data-testid="status"
          className="flex flex-col items-center justify-center w-full h-full gap-3 px-4 text-center"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-gray-500" />
          ) : graphState === 'failed' ? (
            // FS-621: a terminal failure (incl. the reconciler's 'stalled'
            // state, which /status reports as failed) — surface the reason and
            // a retry instead of an infinite spinner.
            <>
              <p className="text-red-600 dark:text-red-400 font-medium">
                {t('data_catalog:graph_failed', 'This graph could not be generated.')}
              </p>
              {graphErrorMessage && (
                <p className="text-sm text-gray-500 dark:text-dark-text-muted max-w-md">
                  {graphErrorMessage}
                </p>
              )}
              {!isReadOnly && (
                <Button variant="outline" onClick={handleRetryProcessing} disabled={retrying}>
                  {retrying
                    ? t('data_catalog:graph_retrying', 'Retrying…')
                    : t('data_catalog:graph_retry', 'Retry processing')}
                </Button>
              )}
            </>
          ) : graphState === 'not_found' ? (
            <p className="text-gray-500 dark:text-dark-text-muted">
              {t('data_catalog:graph_not_found', 'This graph could not be found.')}
            </p>
          ) : (
            // pending / unknown — actually polling now, so this resolves itself.
            <div className="flex items-center">
              <Loader2 className="w-5 h-5 mr-2 animate-spin text-gray-400 dark:text-gray-500" />
              <p className="ml-2 text-gray-500 dark:text-dark-text-muted">
                {t('data_catalog:graph_not_ready', 'Graph is still being processed. Please wait a moment and try again.')}
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* ENG-2588: keep the editor canvas MOUNTED across tab switches (hide
              it off-editor rather than unmounting) so the zoom/pan + selection
              survive — switching to Graph Preview / KG Preview / JSON and back
              no longer resets the view to fit. */}
            <div
              ref={pnidContainerRef}
              data-testid="pnid-renderer-container"
              className={`absolute inset-0 ${activeTab === EDITOR_MODE ? '' : 'hidden'}`}
            >
              <PnidRenderer
                // UI-30: undo/redo render in the zoom pill now, the way the
                // board does it. Same handlers and same enablement as before —
                // only the chrome they are drawn with moved. Read-only editors
                // get none, matching the rail's old `!isReadOnly` gate.
                //
                // Deliberately NOT also gated on `hasBoundingBoxes`, which the
                // rail was (its whole wrapper is `{hasBoundingBoxes && …}`).
                // Deleting the last annotation therefore used to take Undo off
                // screen with it, stranding the one action that brings it back
                // — keyboard-only recovery. The rail could not fix that, since
                // it had nothing left to draw; the pill is always mounted, so
                // it can. The board's undo never disappears either, which is
                // the consistency this ticket is about.
                undoRedo={
                  isReadOnly
                    ? undefined
                    : {
                        onUndo: handleUndo,
                        onRedo: handleRedo,
                        canUndo: canUndoNow,
                        canRedo: canRedoNow,
                      }
                }
                backgroundImage={backgroundImage}
                rectangles={rectangles}
                edges={Array.from(activeEdges.values())}
                nodes={activeNodes}
                initialZoom={initialZoom}
                onClick={handleClick}
                onRectangleDelete={handleDelete}
                onRectangleAdd={handleAdd}
                onImageSize={(size) => setExportImageSize(size)}
                onPolygonAdd={handlePolygonAdd}
                onRectangleUpdate={handleRectangleUpdate}
                onRectangleResizeComplete={handleRectangleResizeComplete}
                mode={mode}
                isReadOnly={isReadOnly || !hasBoundingBoxes}
                // ENG-2588: the canvas highlight follows the open details panel.
                // activeTooltip is the source of truth for "which node is selected"
                // and survives tab switches, so falling back to it keeps the orange
                // outline in sync with the panel (they used to drift apart — the
                // outline reset on a tab round-trip while the panel stayed open).
                selectedNodeId={highlightedNodeId || crossSheetHighlightId || activeTooltip?.nodeId}
                panelSelectedId={activeTooltip?.nodeId}
                isCrossSheetHighlight={!!crossSheetHighlightId}
                hideZoomControls={hideChrome}
                zoomHandleRef={zoomHandleRef}
                onZoomChange={onZoomChange}
                // Draw mode is driven by the chosen SHAPE, not the classification:
                // the Line shape is the polyline/connection tool the moment it's
                // picked (no need to choose a category first), and Rectangle/Polygon
                // are never treated as line mode even with a leftover connection type.
                suppressNodeClickActions={mode === ModeType.Create && addNodeConfig.shape === 'line'}
                disableCreate={mode === ModeType.Create && addNodeConfig.shape === 'line'}
                isPolygonMode={mode === ModeType.Create && addNodeConfig.shape === 'polygon'}
                isPointMode={mode === ModeType.Create && addNodeConfig.shape === 'point'}
                isConnectionMode={mode === ModeType.Create && addNodeConfig.shape === 'line'}
                onConnectionFinish={() => finishConnectionDraw(true)}
                connectionStartPointAbs={connectionStartPointAbs}
                connectionVertices={connectionVertices}
                connectionLineId={connectionLineId}
                connectionHoveredId={connectionHoveredId}
                onConnectionHover={setConnectionHoveredId}
                onSelectionChange={setSelectedNodeIds}
                externalSelectedNodeIds={selectedNodeIds}
                reviewHighlightIds={reviewHighlightNodeIds}
                dimUnmatched={dimUnmatchedHighlights}
                hideAssociationLines={isReviewLayer && !reviewFullColor}
                layers={editorLayers}
                connectionMarkers={connectionMarkers}
                detectedBoundary={detectedBoundary}
                onMarkerDrop={handleMarkerDrop}
              />
              {onNavigateToSheet && totalSheets > 1 && (
                <SheetEdgeNav
                  currentIndex={currentSheetIndex}
                  totalSheets={totalSheets}
                  sheetNames={sheetNames}
                  onNavigate={onNavigateToSheet}
                  connectors={sheetConnectors}
                  multiPageDocs={multiPageDocs}
                  onAddConnector={onAddConnector}
                  onConnectorClick={onConnectorClick}
                  onConnectorContextMenu={onConnectorContextMenu}
                  // The page bar below occupies the top-centre band on V2, so the
                  // top connector lane drops beneath it rather than under it.
                  topOffsetClass={isV2 ? 'top-16' : undefined}
                />
              )}
              {/* Page navigator for multi-sheet drawings. The legacy header
                  carries its own copy (`sheetNavigation` above), so this is
                  V2-only — rendering it in both would double it up.
                  Top-centre, directly under the sub-navbar. SheetEdgeNav's top
                  connector lane shares this band, so it gets pushed below via
                  topOffsetClass whenever this bar is on screen.
                  z-40, not z-30: the "Show Original" layer is an
                  `absolute inset-0 z-30` sibling LATER in the DOM, so an equal
                  z-index loses the tie and the bar disappears behind it. The
                  layer switcher sits at z-40 for the same reason. */}
              {isV2 && onNavigateToSheet && totalSheets > 1 && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 rounded-xl border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card px-3 py-2 shadow-md">
                  <SheetPageBar
                    variant="canvas"
                    currentIndex={currentSheetIndex}
                    total={totalSheets}
                    sheetNames={sheetNames}
                    onNavigate={onNavigateToSheet}
                    disabled={sheetNavDisabled}
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {t('data_catalog:graph_editor.header.sheet_of', {
                      current: currentSheetIndex + 1,
                      total: totalSheets,
                      defaultValue: 'Sheet {{current}} of {{total}}',
                    })}
                  </span>
                </div>
              )}
              {/* The sub-navbar handles search on the V2 editor route; where it
                  isn't mounted (the board card preview) we show the floating bar */}
              {ownsFloatingSearch && (
                <EditorSearch
                  nodes={activeNodes}
                  isOpen={isEditorSearchOpen}
                  onClose={() => {
                    setIsEditorSearchOpen(false)
                    setHighlightedNodeId(null)
                    setSearchMatchNodeIds(new Set())
                  }}
                  onMatchChange={handleSearchMatchChange}
                  listContainer={searchListHost}
                  initialSearch={initialSearch}
                />
              )}
              {/* V2 node details rendered outside this absolute container — see below */}
              {!isV2 && (
                <DetailsFlyout
                  isReadOnly={isReadOnly}
                  node={
                    activeTooltip ? getActiveNodeById(activeTooltip.nodeId) : undefined
                  }
                  updateNode={updateActiveNode}
                  // Only expose delete where it actually works: full edit mode,
                  // or a preview that opts into entity deletion (FS-508). In a
                  // pure read-only view, omitting it hides the dead Remove button.
                  deleteNode={!isReadOnly || allowEntityDeletion ? deleteActiveNodeById : undefined}
                  // Edge deletion stays edit-mode-only. In the preview the node
                  // delete handles its own connected edges, so leave this unset
                  // (which also skips DetailsFlyout's edge-removal loop).
                  deleteEdgeById={!isReadOnly ? deleteActiveEdgeById : undefined}
                  addEdge={addActiveEdge}
                  currentState={activeState}
                  isOpen={!!activeTooltip || isDrawerOpen}
                  onClose={() => {
                    setActiveTooltip(null)
                    setIsDrawerOpen(false)
                    setHighlightedNodeId(null)
                    setIsFromReviewFlow(false)
                  }}
                  showReviewList={isDrawerOpen && !activeTooltip}
                  processedGraphData={processedGraphData}
                  onItemClick={(nodeId) => {
                    const node = getActiveNodeById(nodeId)
                    if (node && node.x !== undefined && node.y !== undefined) {
                      const tooltipData: TooltipData = {
                        nodeId: nodeId,
                        x: node.x,
                        y: node.y,
                      }
                      setActiveTooltip(tooltipData)
                      setIsDrawerOpen(false)
                      setHighlightedNodeId(nodeId)
                      setIsFromReviewFlow(true)
                    }
                  }}
                  onBackToReview={() => {
                    setActiveTooltip(null)
                    setHighlightedNodeId(null)
                    if (activeTooltip) {
                      setIsDrawerOpen(true)
                    } else {
                      setIsDrawerOpen(true)
                    }
                  }}
                  isFromCanvas={!!activeTooltip && !isFromReviewFlow}
                  isFromReviewFlow={isFromReviewFlow}
                  onCategoryClick={() => {
                    setIsDrawerOpen(true)
                  }}
                  onGoToDrawing={onDrawingConnectorClick}
                />
              )}
              {(mode === ModeType.Create || panningFromCreate) && !activeTooltip && !isDrawerOpen && !isV2 && (
                <ControlPanel
                  data-testid="control-panel"
                  onConfigChange={setAddNodeConfig}
                  mode={mode}
                  isOpen={panningFromCreate || undefined}
                  onClose={() => {
                    setHighlightedNodeId(null)
                    setSelectedNodeIds(new Set())
                    setPanningFromCreate(false)
                    setMode(ModeType.Drag)
                  }}
                  hasActiveTooltip={!!activeTooltip}
                  hasPendingPatch={!!pendingGraphPatch}
                  pendingConnectedCount={pendingGraphPatch?.connectedCount ?? 0}
                  onSaveToGraph={handleSaveToGraph}
                  onDiscardDetection={handleDiscardDetection}
                  hasDetectedBoundary={!!detectedBoundary}
                  onUseDetectedBoundary={() => {
                    if (detectedBoundary) {
                      handleAdd({
                        x: detectedBoundary.x,
                        y: detectedBoundary.y,
                        width: detectedBoundary.width,
                        height: detectedBoundary.height,
                      }, true).catch((err) => {
                        console.error('Failed to add node from detected boundary:', err)
                        toast.error(t('graph_editor:editor.applyBoundaryFailed', { defaultValue: 'Failed to apply detected boundary' }))
                      })
                    }
                  }}
                />
              )}
              {hasBoundingBoxes && !hideChrome && (
                isV2 ? (
                  <GraphToolbarV2
                    isReadOnly={isReadOnly}
                    currentMode={mode}
                    onToolChange={handleToolChange}
                    isCommentActive={isCommentPanelOpen}
                    // Only where this editor owns the floating search — the
                    // board card preview and the embed. The full V2 editor's
                    // sub-navbar already carries a search field.
                    onSearchClick={ownsFloatingSearch ? () => setIsEditorSearchOpen(true) : undefined}
                    isSearchActive={isEditorSearchOpen}
                  />
                ) : (
                  <GraphToolbar
                    isReadOnly={isReadOnly}
                    currentMode={mode}
                    onToolChange={handleToolChange}
                    isCommentActive={isCommentPanelOpen}
                    onSearchClick={() => setIsEditorSearchOpen(true)}
                    isSearchActive={isEditorSearchOpen}
                  />
                )
              )}
              <CommentPanel
                workspaceId={graphId}
                isOpen={isCommentPanelOpen}
                onClose={() => setIsCommentPanelOpen(false)}
              />
              {/* ENG-2624: bulk actions moved into the Node Details panel's
                  multi-selection state (MultiSelectPanelV2) — no floating bar. */}
              {/* V2: layer switcher + Original overlay: a 3-way
                  Original|Detected|Review segmented control (PRD "Vision HITL
                  Labeling"). */}
              {isV2 && !hideChrome && (
                <>
                  <div
                    role="radiogroup"
                    aria-label={t('data_catalog:graph_editor.layer_switch', { defaultValue: 'View layer' })}
                    className="absolute top-4 left-4 z-40 flex overflow-hidden rounded-xl border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card shadow-md text-sm font-medium"
                  >
                    {([
                      ['original', t('data_catalog:graph_editor.layer_original', { defaultValue: 'Original' })],
                      ['detected', t('data_catalog:graph_editor.layer_detected', { defaultValue: 'Detected' })],
                      ['review', t('data_catalog:graph_editor.layer_review', { defaultValue: 'Review' })],
                    ] as [PnidLayer, string][]).map(([layer, label]) => (
                      <button
                        key={layer}
                        type="button"
                        role="radio"
                        onClick={() => setPnidLayer(layer)}
                        aria-checked={pnidLayer === layer}
                        className={`px-3 py-2 transition-colors ${
                          pnidLayer === layer
                            ? 'bg-[#0e7490] dark:bg-brand-light text-white'
                            : 'text-foreground hover:bg-black/5 dark:hover:bg-white/5'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {!showOriginal && <EditorLayersMenu layers={editorLayers} onChange={setEditorLayers} />}
                  {showOriginal && (
                    <div className="absolute inset-0 z-30 bg-white dark:bg-dark-card">
                      <OriginalDocumentViewer
                        pnidId={pnidId}
                        pageNumber={documentId ? parseInt(searchParams.get('page') || '1', 10) : undefined}
                      />
                    </div>
                  )}
                  {isReviewLayer && (
                    <ReviewControls
                      threshold={reviewThreshold}
                      onThresholdChange={setReviewThreshold}
                      fullColor={reviewFullColor}
                      onFullColorChange={setReviewFullColor}
                      symbolsOnly={reviewSymbolsOnly}
                      onSymbolsOnlyChange={setReviewSymbolsOnly}
                      items={flaggedReviewItems}
                      currentIndex={currentReviewIndex}
                      onJump={jumpToReviewItem}
                      onPrev={() => jumpToReviewItem(currentReviewIndex < 0 ? flaggedReviewItems.length - 1 : currentReviewIndex - 1)}
                      onNext={() => jumpToReviewItem(currentReviewIndex < 0 ? 0 : currentReviewIndex + 1)}
                    />
                  )}
                </>
              )}
            </div>
          {activeTab === GRAPH_PREVIEW && (
            <div
              data-testid="graph-preview-container"
              className="relative h-full"
            >
              {/* Same pill geometry and type as the sub-navbar tabs; the selected view takes the theme primary. */}
              <div className="absolute bottom-3 right-3 z-10 flex h-8 items-center rounded-lg border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card p-[3px] shadow-sm">
                {([
                  ['clean', t('data_catalog:graph_editor.preview_view.clean', { defaultValue: 'Clean redraw' })],
                  ['lines', t('data_catalog:graph_editor.preview_view.lines', { defaultValue: 'Line map' })],
                  ['cards', t('data_catalog:graph_editor.preview_view.cards', { defaultValue: 'Cards' })],
                ] as const).map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={previewView === v}
                    onClick={() => setPreviewView(v)}
                    className={`h-full flex items-center px-3 text-sm font-medium whitespace-nowrap rounded-md transition-all ${previewView === v ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="w-full h-full">
                {previewView !== 'cards' ? (
                  // same graph source as the Cards view below: local state only in the fabric embed
                  <CleanRedrawPreview
                    nodes={isReadOnly && isFabricEmbedMode() ? localNodes : currentState.nodes}
                    edges={isReadOnly && isFabricEmbedMode() ? localEdges : currentState.edges}
                    view={previewView}
                    onDrawingConnectorClick={onDrawingConnectorClick}
                  />
                ) : (
                <KnowledgeGraph
                  externalSearchTerm={isV2 ? externalSearchTerm : undefined}
                  onSearchMatchInfo={isV2 ? onSearchMatchInfo : undefined}
                  onSearchNext={isV2 ? onSearchNext : undefined}
                  onSearchPrevious={isV2 ? onSearchPrevious : undefined}
                  // Fabric embed is the only read-only caller that exposes the
                  // Graph Preview tab today (board / catalog previews pin
                  // activeTab to EDITOR_MODE). The embed's read-only path stores
                  // the graph in local state to avoid cross-contaminating the
                  // global Zustand store across multiple P&IDs, so we plumb
                  // localNodes/localEdges into KnowledgeGraph here. The
                  // isFabricEmbedMode() gate keeps this scoped to the embed —
                  // if a non-embed read-only consumer ever wires up Graph
                  // Preview, it keeps the existing global-store behavior until
                  // we deliberately opt it in.
                  graph={isReadOnly && isFabricEmbedMode() ? { nodes: localNodes, edges: localEdges } : undefined}
                  readOnly={isReadOnly && isFabricEmbedMode()}
                  onDrawingConnectorClick={onDrawingConnectorClick}
                />
                )}
              </div>
            </div>
          )}
          {activeTab === JSON_VIEW && (
            <div
              data-testid="json-view-container"
              className={`relative h-full ${!isV2 && graphDocument && !isReadOnly && hasBoundingBoxes ? 'pt-[60px]' : ''}`}
            >
              <JsonViewer
                data={jsonMode === 'gt' ? goldDrawingData : parsedGraphData}
                // VISN-319: placeholders and the "no ground truth yet" note must
                // not be downloadable — a note object saved as *.json would
                // masquerade as a real export.
                downloadable={
                  jsonMode === 'gt'
                    ? Boolean((goldDrawingData as { drawing_key?: unknown } | null)?.drawing_key)
                    : parsedV1?.graphId === graphId && parsedV1.ok
                }
                onDownloadOverride={jsonMode === 'gt' ? downloadStoredGroundTruth : undefined}
                title='JSON'
                externalSearchTerm={isV2 && activeTab === JSON_VIEW ? externalSearchTerm : undefined}
                onSearchMatchInfo={isV2 && activeTab === JSON_VIEW ? onSearchMatchInfo : undefined}
                onSearchNext={isV2 && activeTab === JSON_VIEW ? onSearchNext : undefined}
                onSearchPrevious={isV2 && activeTab === JSON_VIEW ? onSearchPrevious : undefined}
              />
            </div>
          )}
          {activeTab === ORIGINAL_VIEW && (
            <div
              data-testid="original-view-container"
              className="relative h-full p-4"
            >
              <OriginalDocumentViewer
                pnidId={pnidId}
                pageNumber={pnidPageNumber ?? (documentId ? parseInt(searchParams.get('page') || '1', 10) : undefined)}
              />
            </div>
          )}
          {IO_LINE_TRACING_ENABLED && activeTab === LINES_VIEW && (
            <div
              data-testid="lines-view-container"
              className={`relative h-full ${graphDocument && !isReadOnly && hasBoundingBoxes ? 'pt-[60px]' : ''}`}
            >
              {pnidId ? (
                <LinesTab documentId={pnidId} />
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-gray-500 dark:text-dark-text-muted">
                  P&amp;ID document not loaded yet.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
    {/* FS-657: search match list — flex sibling so the canvas resizes rather
        than the list covering the drawing. Auto width, so the column is 0px
        wide until the list is portalled in. */}
    <div ref={setSearchListHost} className="shrink-0 flex" data-testid="editor-search-list-host" />
    {/* V2: Node details panel — flex sibling so canvas resizes */}
    {showNodeDetailsPanel && (
      <NodeDetailsPanelV2
        node={nodeDetailsPanelNode}
        isOpen={true}
        onClose={() => {
          setActiveTooltip(null)
          setHighlightedNodeId(null)
        }}
        updateNode={updateActiveNode}
        deleteNode={deleteActiveNodeById}
        deleteEdgeById={deleteActiveEdgeById}
        currentState={activeState}
        onBeforeRemove={() => {
          // Snapshot before the panel's Remove so a single Undo restores this node + its edges.
          recordCanvasSnapshot()
        }}
      />
    )}
    {/* ENG-2624: Node Details panel in its multi-selection (bulk) state. */}
    {showMultiSelectPanel && (
      <MultiSelectPanelV2
        // Key by selection membership so the classification dropdowns reset when
        // the user swaps to a different set of nodes — otherwise stale values
        // could be applied to the new batch (ENG-2624).
        key={Array.from(selectedNodeIds).sort().join(',')}
        count={selectedNodeIds.size}
        onClose={() => setSelectedNodeIds(new Set())}
        onApplyClassification={handleBulkClassify}
        onDelete={handleBulkDelete}
        onFillField={handleBulkFillField}
        onClearField={handleBulkClearField}
      />
    )}
    {/* V2: Add Node panel — flex sibling so canvas resizes */}
    {showAddNodePanel && (
      <AddNodePanelV2
        isOpen={true}
        initialConfig={addObjectDraft}
        onClose={() => {
          // Backing out of add mode finalizes any in-progress connection (keeps a
          // valid line, discards a degenerate one) — parity with a tool switch.
          finishConnectionDraw()
          setHighlightedNodeId(null)
          setSelectedNodeIds(new Set())
          setMode(ModeType.Drag)
          onCreateModeExit?.()
        }}
        onConfigChange={(config) => {
          // Re-selecting a tool in the Add-Object panel starts a fresh action, so
          // finish any in-progress connection draw — this is how the
          // user "starts a new connection": a valid line stays committed (a
          // degenerate one is discarded) and the next canvas click begins a new one.
          finishConnectionDraw()
          // Carries the pre-filled classification, Tag, and Note onto the next
          // drawn node, and derives the node kind from the classification.
          // (Tag and Note used to be dropped here.)
          setAddNodeConfig(
            panelConfigToAddNodeConfig(config, discipline),
          )
          // ENG-2631: remember the raw panel selection so it survives a close/reopen.
          setAddObjectDraft(config)
        }}
      />
    )}
    </div>
    </TaxonomyProvider>
  )
}

export default EditorWrapper
